import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ApiIcon from '@mui/icons-material/Api';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityIcon from '@mui/icons-material/Visibility';

import {
  listFlights,
  fetchFlight,
  createFlight,
  updateFlight,
  generateFlightTasks,
  archiveFlight,
  listCreativeTasks,
  fetchCreativeTask,
  createCreativeTask,
  updateCreativeTask,
  assignCreativeTask,
  startCreativeTask,
  submitCreativeTask,
  requestRevisionCreativeTask,
  approveCreativeTask,
  addCreativeTaskComment,
  uploadCreativeTaskFile,
  listAvatars,
  createAvatar,
  updateAvatar,
  listPainPoints,
  createPainPoint,
  updatePainPoint,
  listAngleChoices,
  createAngleChoice,
  updateAngleChoice,
  listAdTypeChoices,
  createAdTypeChoice,
  updateAdTypeChoice,
  listStyleChoices,
  createStyleChoice,
  updateStyleChoice,
  listDesignerProfiles,
  createDesignerProfile,
  updateDesignerProfile,
  listDesignerGroups,
  createDesignerGroup,
  updateDesignerGroup,
  fetchClientVerticals,
  bulkUpdateTasks,
  generateScript,
  listScriptPrompts,
  updateScriptPrompt,
  exportFlightTasks,
  getDesignerAPIToken,
  regenerateDesignerAPIToken,
  launchTaskToFacebook,
  fetchLaunchPrefill,
  fetchFacebookAdStructure,
  generateAdCopy,
  listAdCopyPrompts,
  updateAdCopyPrompt,
} from '@src/api/creativeStudio';
import { fetchVerticals } from '@src/api/actions/verticals';
import { fetchClientCreativeKnowledge } from '@src/api';
import { listAssignmentUsers } from '@src/api/tasks';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { useAuth } from '@src/context';

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All Ages' },
  { value: '55+', label: '55+' },
  { value: 'men', label: 'Men' },
  { value: 'women', label: 'Women' },
];

const STATUS_CHIP_SX = {
  draft: { bgcolor: 'grey.300', color: 'grey.800' },
  assigned: { bgcolor: '#c8e6c9', color: '#2e7d32' },       // light green
  in_progress: { bgcolor: '#fff9c4', color: '#f57f17' },     // yellow
  submitted: { bgcolor: '#bbdefb', color: '#1565c0' },       // blue
  needs_revision: { bgcolor: '#ffcdd2', color: '#c62828' },  // red
  approved: { bgcolor: '#4caf50', color: '#fff' },            // green
  complete: { bgcolor: '#4caf50', color: '#fff' },            // green
  cancelled: { bgcolor: 'grey.300', color: 'grey.800' },
};

const FLIGHT_STATUS_COLORS = {
  draft: 'default',
  in_progress: 'warning',
  complete: 'success',
  cancelled: 'default',
};

function getApiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (data && typeof data === 'object') {
    const firstValue = Object.values(data).find((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v)));
    if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0]);
    if (typeof firstValue === 'string' && firstValue.trim()) return firstValue;
  }
  return fallback;
}

const autoSlug = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 20);

export default function CreativeTasksPage() {
  const { state } = useAuth();
  const user = state?.user;
  const isStaff = user?.is_staff || user?.is_superuser;

  // Client verticals cache (from creative knowledge)
  const clientVerticalsCacheRef = useRef({});
  const [clientVerticalsVersion, setClientVerticalsVersion] = useState(0);

  const loadClientVerticals = useCallback(async (clientId) => {
    if (!clientId || clientVerticalsCacheRef.current[clientId]) return;
    try {
      const res = await fetchClientVerticals(clientId);
      const data = Array.isArray(res.data) ? res.data : [];
      // Extract unique verticals from creative knowledge records
      const verticalIds = new Set();
      data.forEach((ck) => {
        (ck.verticals || []).forEach((vId) => verticalIds.add(vId));
      });
      clientVerticalsCacheRef.current[clientId] = [...verticalIds];
      setClientVerticalsVersion((v) => v + 1);
    } catch (_err) {
      // Fallback: show all verticals
      clientVerticalsCacheRef.current[clientId] = null;
    }
  }, []);

  const getVerticalsForClient = (clientId) => {
    const cached = clientVerticalsCacheRef.current[clientId];
    if (!cached) return verticals; // fallback: show all
    return verticals.filter((v) => cached.includes(v.id));
  };

  // Flights
  const [flights, setFlights] = useState([]);
  const [flightsLoading, setFlightsLoading] = useState(false);
  const [expandedFlightId, setExpandedFlightId] = useState(null);
  const [flightTasks, setFlightTasks] = useState({});
  const [flightTasksLoading, setFlightTasksLoading] = useState({});
  const [error, setError] = useState('');

  // Filters
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDesigner, setFilterDesigner] = useState('');
  const [showArchivedFlights, setShowArchivedFlights] = useState(false);

  // Lookups
  const [verticals, setVerticals] = useState([]);
  const avatarsCacheRef = useRef({}); // { verticalId: [...] } — ref to avoid re-renders
  const painPointsCacheRef = useRef({});
  const [verticalOptionsVersion, setVerticalOptionsVersion] = useState(0); // bump to trigger re-render when cache fills
  const [angles, setAngles] = useState([]);
  const [adTypes, setAdTypes] = useState([]);
  const [styles, setStyles] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [designerGroups, setDesignerGroups] = useState([]);

  // Create flight dialog
  const [createFlightOpen, setCreateFlightOpen] = useState(false);
  const [creatingFlight, setCreatingFlight] = useState(false);
  const [createFlightError, setCreateFlightError] = useState('');
  const [flightForm, setFlightForm] = useState({
    name: '', designer: '', designer_group: '', start_date: '', end_date: '', notes: '',
  });
  const [flightSegments, setFlightSegments] = useState([{ client: '', vertical: '', count: 5 }]);

  // Generate tasks for existing flight
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generatingTasks, setGeneratingTasks] = useState(false);
  const [generateFlightId, setGenerateFlightId] = useState(null);
  const [generateSegments, setGenerateSegments] = useState([{ client: '', vertical: '', count: 5 }]);

  // Edit task dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState('');
  const [editForm, setEditForm] = useState({});
  const [editAvatars, setEditAvatars] = useState([]);
  const [editPainPoints, setEditPainPoints] = useState([]);

  // Bulk select + apply
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [bulkApplyOpen, setBulkApplyOpen] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkApplyFlightId, setBulkApplyFlightId] = useState(null);
  const [bulkForm, setBulkForm] = useState({ vertical: '', avatar: '', pain_point: '', angle: '', ad_type: '', style: '', audience: '', designer: '' });
  const [bulkAvatars, setBulkAvatars] = useState([]);
  const [bulkPainPoints, setBulkPainPoints] = useState([]);

  // Script generator
  const [scriptGenerating, setScriptGenerating] = useState(false);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commenting, setCommenting] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [revisionText, setRevisionText] = useState('');
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitComment, setSubmitComment] = useState('');

  // API modal
  const [apiModalOpen, setApiModalOpen] = useState(false);
  const [apiToken, setApiToken] = useState(null);
  const [apiTokenLoading, setApiTokenLoading] = useState(false);

  // Launch to Facebook
  const [fbLaunchOpen, setFbLaunchOpen] = useState(false);
  const [fbLaunchTask, setFbLaunchTask] = useState(null);
  const [fbStructure, setFbStructure] = useState([]);
  const [fbStructureLoading, setFbStructureLoading] = useState(false);
  const [fbLaunchForm, setFbLaunchForm] = useState({ ad_account_id: '', campaign_id: '', ad_set_ids: [], headline: '', primary_text: '', description: '', cta_type: 'LEARN_MORE', destination_url: '', display_link: '', page_id: '' });
  const [fbLaunchLoading, setFbLaunchLoading] = useState(false);
  const [fbLaunchResult, setFbLaunchResult] = useState(null);
  const [fbCopyGenerating, setFbCopyGenerating] = useState(false);
  const [fbHeadlineOptions, setFbHeadlineOptions] = useState([]);
  const [fbShowInactive, setFbShowInactive] = useState(false);
  const [approveRatingOpen, setApproveRatingOpen] = useState(false);
  const [approveRating, setApproveRating] = useState(0);

  // Manage lookups
  const [manageOpen, setManageOpen] = useState(false);
  const [manageTab, setManageTab] = useState(0);
  const [manageVertical, setManageVertical] = useState('');
  const [manageAvatars, setManageAvatars] = useState([]);
  const [managePainPoints, setManagePainPoints] = useState([]);
  const [manageAngles, setManageAngles] = useState([]);
  const [manageAdTypes, setManageAdTypes] = useState([]);
  const [manageStyles, setManageStyles] = useState([]);
  const [manageDesigners, setManageDesigners] = useState([]);
  const [manageGroups, setManageGroups] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageSaving, setManageSaving] = useState(false);
  const [manageError, setManageError] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [scriptPrompt, setScriptPrompt] = useState(null);
  const [scriptPromptDraft, setScriptPromptDraft] = useState('');
  const [adCopyPrompt, setAdCopyPrompt] = useState(null);
  const [adCopyPromptDraft, setAdCopyPromptDraft] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [designerDialogOpen, setDesignerDialogOpen] = useState(false);
  const [editingDesigner, setEditingDesigner] = useState(null);

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue: findClientOptionByValue,
    addOption: addClientOption,
  } = useClientAutocomplete();

  // --- Load lookups on mount ---
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [vertRes, angleRes, adTypeRes, styleRes, designerRes, groupRes, ckRes] = await Promise.all([
          fetchVerticals(), listAngleChoices(), listAdTypeChoices(), listStyleChoices(), listDesignerProfiles(), listDesignerGroups(), fetchClientCreativeKnowledge(),
        ]);
        const allVerticals = Array.isArray(vertRes.data) ? vertRes.data : [];
        const ckEntries = Array.isArray(ckRes.data) ? ckRes.data : [];
        const activeVerticalIds = new Set();
        ckEntries.forEach((entry) => {
          (entry.verticals || []).forEach((v) => { if (v.id) activeVerticalIds.add(v.id); });
        });
        setVerticals(activeVerticalIds.size > 0 ? allVerticals.filter((v) => activeVerticalIds.has(v.id)) : allVerticals);
        setAngles(Array.isArray(angleRes.data) ? angleRes.data : []);
        setAdTypes(Array.isArray(adTypeRes.data) ? adTypeRes.data : []);
        setStyles(Array.isArray(styleRes.data) ? styleRes.data : []);
        setDesigners(Array.isArray(designerRes.data) ? designerRes.data : []);
        setDesignerGroups(Array.isArray(groupRes.data) ? groupRes.data : []);
      } catch (_err) { /* keep usable */ }
    };
    loadLookups();
    loadFlights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Flight handlers ---
  const loadFlights = async () => {
    setFlightsLoading(true);
    setError('');
    try {
      const res = await listFlights({
        client_id: filterClient || undefined,
        status: filterStatus || undefined,
        designer_id: filterDesigner || undefined,
        ...(showArchivedFlights ? { include_archived: 'true' } : {}),
      });
      const data = Array.isArray(res.data) ? res.data : [];
      data.forEach((f) => { if (f.client && f.client_name) addClientOption({ value: f.client, label: f.client_name }); });
      setFlights(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load flights'));
    } finally {
      setFlightsLoading(false);
    }
  };

  const loadFlightTasks = async (flightId) => {
    setFlightTasksLoading((prev) => ({ ...prev, [flightId]: true }));
    try {
      const res = await listCreativeTasks({ flight_id: flightId });
      const tasks = Array.isArray(res.data) ? res.data : [];
      setFlightTasks((prev) => ({ ...prev, [flightId]: tasks }));
      // Preload avatar/pain point options for all verticals used in these tasks
      const verticalIds = [...new Set(tasks.map((t) => t.vertical).filter(Boolean))];
      verticalIds.forEach((vId) => loadVerticalOptions(vId));
      // Preload client verticals for all clients in these tasks
      const clientIds = [...new Set(tasks.map((t) => t.client).filter(Boolean))];
      clientIds.forEach((cId) => loadClientVerticals(cId));
    } catch (_err) { /* keep usable */ }
    finally {
      setFlightTasksLoading((prev) => ({ ...prev, [flightId]: false }));
    }
  };

  const handleExpandFlight = (flightId) => {
    if (expandedFlightId === flightId) {
      setExpandedFlightId(null);
      return;
    }
    setExpandedFlightId(flightId);
    loadFlightTasks(flightId);
  };

  // --- Inline edit helpers ---
  const loadVerticalOptions = useCallback(async (verticalId) => {
    if (!verticalId || avatarsCacheRef.current[verticalId]) return;
    try {
      const [aRes, pRes] = await Promise.all([
        listAvatars({ vertical_id: verticalId }),
        listPainPoints({ vertical_id: verticalId }),
      ]);
      avatarsCacheRef.current[verticalId] = Array.isArray(aRes.data) ? aRes.data : [];
      painPointsCacheRef.current[verticalId] = Array.isArray(pRes.data) ? pRes.data : [];
      setVerticalOptionsVersion((v) => v + 1); // trigger re-render so rows pick up new options
    } catch (_err) { /* keep usable */ }
  }, []);

  const refreshTimerRef = useRef(null);
  const refreshFlightCountsQuietly = useCallback(() => {
    // Debounce — don't fire on every single cell change
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const res = await listFlights({
          client_id: filterClient || undefined,
          status: filterStatus || undefined,
          designer_id: filterDesigner || undefined,
        });
        const data = Array.isArray(res.data) ? res.data : [];
        setFlights((prev) => {
          // Only update if counts actually changed to avoid unnecessary re-renders
          let changed = false;
          const next = prev.map((f) => {
            const u = data.find((d) => d.id === f.id);
            if (!u) return f;
            if (f.task_count !== u.task_count || f.completed_task_count !== u.completed_task_count || f.approved_task_count !== u.approved_task_count) {
              changed = true;
              return { ...f, task_count: u.task_count, completed_task_count: u.completed_task_count, approved_task_count: u.approved_task_count, status: u.status, status_display: u.status_display };
            }
            return f;
          });
          return changed ? next : prev;
        });
      } catch (_err) { /* silent */ }
    }, 1000);
  }, [filterClient, filterStatus, filterDesigner, showArchivedFlights]);

  const handleInlineUpdate = async (taskId, flightId, field, value) => {
    const payload = { [field]: value || null };
    if (field === 'vertical') {
      payload.avatar = null;
      payload.pain_point = null;
      if (value) loadVerticalOptions(value);
    }
    // Optimistic local update
    setFlightTasks((prev) => ({
      ...prev,
      [flightId]: (prev[flightId] || []).map((t) => (t.id === taskId ? { ...t, ...payload } : t)),
    }));
    try {
      const res = await updateCreativeTask(taskId, payload);
      // Merge only the computed fields from server response
      if (res.data) {
        setFlightTasks((prev) => {
          const tasks = prev[flightId] || [];
          const idx = tasks.findIndex((t) => t.id === taskId);
          if (idx === -1) return prev;
          const merged = { ...tasks[idx], ...res.data };
          // Skip update if nothing meaningful changed
          if (tasks[idx].is_complete === merged.is_complete && tasks[idx].status === merged.status && tasks[idx].structured_name === merged.structured_name) {
            return prev;
          }
          const next = [...tasks];
          next[idx] = merged;
          return { ...prev, [flightId]: next };
        });
      }
      refreshFlightCountsQuietly();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to update task'));
      loadFlightTasks(flightId);
    }
  };

  const handleInlineHookUpdate = async (taskId, flightId, hookText, hookSlug) => {
    setFlightTasks((prev) => ({
      ...prev,
      [flightId]: (prev[flightId] || []).map((t) => (t.id === taskId ? { ...t, hook_text: hookText, hook_slug: hookSlug } : t)),
    }));
    try {
      const res = await updateCreativeTask(taskId, { hook_text: hookText, hook_slug: hookSlug });
      if (res.data) {
        setFlightTasks((prev) => ({
          ...prev,
          [flightId]: (prev[flightId] || []).map((t) => (t.id === taskId ? { ...t, ...res.data } : t)),
        }));
      }
      refreshFlightCountsQuietly();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to update hook'));
      loadFlightTasks(flightId);
    }
  };

  // --- Bulk select + apply ---
  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      next.has(taskId) ? next.delete(taskId) : next.add(taskId);
      return next;
    });
  };

  const toggleAllInFlight = (flightId) => {
    const tasks = flightTasks[flightId] || [];
    const editableTasks = tasks.filter((t) => ['draft', 'assigned'].includes(t.status));
    const allSelected = editableTasks.every((t) => selectedTaskIds.has(t.id));
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      editableTasks.forEach((t) => allSelected ? next.delete(t.id) : next.add(t.id));
      return next;
    });
  };

  const openBulkApply = (flightId) => {
    setBulkApplyFlightId(flightId);
    setBulkForm({ vertical: '', avatar: '', pain_point: '', angle: '', ad_type: '', style: '', audience: '', designer: '' });
    setBulkAvatars([]);
    setBulkPainPoints([]);
    setBulkApplyOpen(true);
  };

  const handleBulkVerticalChange = async (verticalId) => {
    setBulkForm((p) => ({ ...p, vertical: verticalId, avatar: '', pain_point: '' }));
    if (verticalId) {
      try {
        const [aR, pR] = await Promise.all([listAvatars({ vertical_id: verticalId }), listPainPoints({ vertical_id: verticalId })]);
        setBulkAvatars(Array.isArray(aR.data) ? aR.data : []);
        setBulkPainPoints(Array.isArray(pR.data) ? pR.data : []);
      } catch (_err) { /* keep usable */ }
    } else { setBulkAvatars([]); setBulkPainPoints([]); }
  };

  const handleBulkApply = async () => {
    const ids = [...selectedTaskIds];
    if (!ids.length) return;
    const payload = { task_ids: ids };
    Object.entries(bulkForm).forEach(([k, v]) => { if (v) payload[k] = v; });
    if (Object.keys(payload).length <= 1) return; // only task_ids, no fields
    setBulkApplying(true);
    try {
      await bulkUpdateTasks(payload);
      setBulkApplyOpen(false);
      setSelectedTaskIds(new Set());
      if (bulkApplyFlightId) await loadFlightTasks(bulkApplyFlightId);
      refreshFlightCountsQuietly();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to bulk update'));
    } finally {
      setBulkApplying(false);
    }
  };

  // --- Script generator ---
  const handleGenerateScript = async (taskId) => {
    setScriptGenerating(true);
    try {
      await generateScript(taskId, {});
      // Re-fetch the full task to get updated script, hook, and client_context
      const res = await fetchCreativeTask(taskId);
      setDetailTask(res.data);
      // Refresh the flight task list in the background
      if (expandedFlightId) loadFlightTasks(expandedFlightId);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to generate script'));
    } finally {
      setScriptGenerating(false);
    }
  };

  const validSegments = (segs) => segs.filter((s) => s.client && s.vertical && s.count > 0);
  const totalSegmentCount = (segs) => validSegments(segs).reduce((sum, s) => sum + (parseInt(s.count, 10) || 0), 0);

  const handleCreateFlight = async () => {
    setCreatingFlight(true);
    setCreateFlightError('');
    try {
      // Create the flight
      const flightRes = await createFlight({
        name: flightForm.name,
        designer: flightForm.designer || null,
        designer_group: flightForm.designer_group || null,
        start_date: flightForm.start_date,
        end_date: flightForm.end_date,
        notes: flightForm.notes,
        target_count: totalSegmentCount(flightSegments),
      });
      const flightId = flightRes.data.id;

      // Generate tasks from segments
      const segs = validSegments(flightSegments);
      if (segs.length) {
        await generateFlightTasks(flightId, {
          segments: segs.map((s) => ({ client: s.client, vertical: s.vertical, count: parseInt(s.count, 10) })),
        });
      }

      setCreateFlightOpen(false);
      setFlightForm({ name: '', designer: '', start_date: '', end_date: '', notes: '' });
      setFlightSegments([{ client: '', vertical: '', count: 5 }]);
      await loadFlights();
      setExpandedFlightId(flightId);
      loadFlightTasks(flightId);
    } catch (err) {
      setCreateFlightError(getApiErrorMessage(err, 'Failed to create flight'));
    } finally {
      setCreatingFlight(false);
    }
  };

  const handleGenerateTasks = async () => {
    if (!generateFlightId) return;
    setGeneratingTasks(true);
    setError('');
    try {
      const segs = validSegments(generateSegments);
      if (!segs.length) return;
      await generateFlightTasks(generateFlightId, {
        segments: segs.map((s) => ({ client: s.client, vertical: s.vertical, count: parseInt(s.count, 10) })),
      });
      setGenerateOpen(false);
      setGenerateSegments([{ client: '', vertical: '', count: 5 }]);
      await loadFlights();
      await loadFlightTasks(generateFlightId);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to generate tasks'));
    } finally {
      setGeneratingTasks(false);
    }
  };

  // --- Task handlers ---
  const openDetail = async (taskId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setCommentText('');
    try {
      const res = await fetchCreativeTask(taskId);
      setDetailTask(res.data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load task'));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAction = async (actionFn, taskId, label) => {
    setActionLoading(label);
    try {
      const res = await actionFn(taskId);
      setDetailTask(res.data);
      if (expandedFlightId) await loadFlightTasks(expandedFlightId);
      await loadFlights();
    } catch (err) {
      setError(getApiErrorMessage(err, `Failed to ${label}`));
    } finally {
      setActionLoading('');
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !detailTask) return;
    setCommenting(true);
    try {
      await addCreativeTaskComment(detailTask.id, { body: commentText.trim() });
      const res = await fetchCreativeTask(detailTask.id);
      setDetailTask(res.data);
      setCommentText('');
    } catch (err) { setError(getApiErrorMessage(err, 'Failed to add comment')); }
    finally { setCommenting(false); }
  };

  const handleSubmitTask = async () => {
    if (!detailTask) return;
    setActionLoading('submit');
    try {
      // Ensure frame_io_url is saved before submitting
      const currentUrl = (detailTask.frame_io_url || '').trim();
      if (currentUrl && currentUrl !== (detailTask._saved_frame_io_url || '')) {
        await updateCreativeTask(detailTask.id, { frame_io_url: currentUrl });
      }
      const res = await submitCreativeTask(detailTask.id, {
        comment: submitComment.trim() || undefined,
      });
      setDetailTask(res.data);
      setSubmitOpen(false);
      setSubmitComment('');
      if (expandedFlightId) loadFlightTasks(expandedFlightId);
      refreshFlightCountsQuietly();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to submit'));
    } finally {
      setActionLoading('');
    }
  };

  const handleRequestRevision = async () => {
    if (!revisionText.trim() || !detailTask) return;
    setActionLoading('revision');
    try {
      const res = await requestRevisionCreativeTask(detailTask.id, { body: revisionText.trim() });
      setDetailTask(res.data);
      setRevisionOpen(false);
      setRevisionText('');
      if (expandedFlightId) await loadFlightTasks(expandedFlightId);
      await loadFlights();
    } catch (err) { setError(getApiErrorMessage(err, 'Failed to request revision')); }
    finally { setActionLoading(''); }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !detailTask) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', 'deliverable');
    setActionLoading('upload');
    try {
      await uploadCreativeTaskFile(detailTask.id, formData);
      const res = await fetchCreativeTask(detailTask.id);
      setDetailTask(res.data);
    } catch (err) { setError(getApiErrorMessage(err, 'Failed to upload file')); }
    finally { setActionLoading(''); event.target.value = ''; }
  };

  // --- Edit task ---
  const openEditTask = async (task) => {
    setEditForm({
      id: task.id,
      client: task.client,
      vertical: task.vertical || '',
      avatar: task.avatar || '',
      pain_point: task.pain_point || '',
      angle: task.angle || '',
      hook_text: task.hook_text || '',
      hook_slug: task.hook_slug || '',
      ad_type: task.ad_type || '',
      style: task.style || '',
      audience: task.audience || 'all',
      designer: task.designer || '',
      script_text: task.script_text || '',
      frame_io_url: task.frame_io_url || '',
      notes: task.notes || '',
    });
    setEditError('');
    setEditOpen(true);
    if (task.vertical) {
      try {
        const [avatarRes, painRes] = await Promise.all([
          listAvatars({ vertical_id: task.vertical }),
          listPainPoints({ vertical_id: task.vertical }),
        ]);
        setEditAvatars(Array.isArray(avatarRes.data) ? avatarRes.data : []);
        setEditPainPoints(Array.isArray(painRes.data) ? painRes.data : []);
      } catch (_err) { /* keep usable */ }
    }
  };

  const handleEditVerticalChange = async (verticalId) => {
    setEditForm((prev) => ({ ...prev, vertical: verticalId, avatar: '', pain_point: '' }));
    if (verticalId) {
      try {
        const [avatarRes, painRes] = await Promise.all([
          listAvatars({ vertical_id: verticalId }), listPainPoints({ vertical_id: verticalId }),
        ]);
        setEditAvatars(Array.isArray(avatarRes.data) ? avatarRes.data : []);
        setEditPainPoints(Array.isArray(painRes.data) ? painRes.data : []);
      } catch (_err) { /* keep usable */ }
    } else { setEditAvatars([]); setEditPainPoints([]); }
  };

  const handleSaveEdit = async () => {
    setEditing(true);
    setEditError('');
    try {
      const { id, ...payload } = editForm;
      await updateCreativeTask(id, payload);
      setEditOpen(false);
      if (detailTask && detailTask.id === id) {
        const res = await fetchCreativeTask(id);
        setDetailTask(res.data);
      }
      if (expandedFlightId) await loadFlightTasks(expandedFlightId);
      await loadFlights();
    } catch (err) { setEditError(getApiErrorMessage(err, 'Failed to update task')); }
    finally { setEditing(false); }
  };

  // --- Manage lookups handlers (kept from before) ---
  const loadManageVerticalLookups = async (verticalId, archived) => {
    if (!verticalId) { setManageAvatars([]); setManagePainPoints([]); return; }
    setManageLoading(true);
    setManageError('');
    try {
      const params = { vertical_id: verticalId, ...(archived ? { include_archived: 'true' } : {}) };
      const [avatarRes, painRes] = await Promise.all([listAvatars(params), listPainPoints(params)]);
      setManageAvatars(Array.isArray(avatarRes.data) ? avatarRes.data : []);
      setManagePainPoints(Array.isArray(painRes.data) ? painRes.data : []);
    } catch (err) { setManageError(getApiErrorMessage(err, 'Failed to load lookup data')); }
    finally { setManageLoading(false); }
  };

  const loadManageGlobalLookups = async (archived) => {
    setManageLoading(true);
    setManageError('');
    try {
      const params = archived ? { include_archived: 'true' } : {};
      const [angleRes, adTypeRes, styleRes, designerRes, groupRes, usersRes, promptRes, adCopyPromptRes] = await Promise.all([
        listAngleChoices(params), listAdTypeChoices(params), listStyleChoices(params),
        listDesignerProfiles(params), listDesignerGroups(params), listAssignmentUsers(), listScriptPrompts(), listAdCopyPrompts(),
      ]);
      setManageAngles(Array.isArray(angleRes.data) ? angleRes.data : []);
      setManageAdTypes(Array.isArray(adTypeRes.data) ? adTypeRes.data : []);
      setManageStyles(Array.isArray(styleRes.data) ? styleRes.data : []);
      setManageDesigners(Array.isArray(designerRes.data) ? designerRes.data : []);
      setManageGroups(Array.isArray(groupRes.data) ? groupRes.data : []);
      setStaffUsers(Array.isArray(usersRes.data) ? usersRes.data : Array.isArray(usersRes.data?.results) ? usersRes.data.results : []);
      const prompts = Array.isArray(promptRes.data) ? promptRes.data : [];
      const defaultPrompt = prompts.find((p) => p.is_default) || prompts[0] || null;
      setScriptPrompt(defaultPrompt);
      setScriptPromptDraft(defaultPrompt?.system_prompt || '');
      const adCopyPrompts = Array.isArray(adCopyPromptRes.data) ? adCopyPromptRes.data : [];
      const defaultAdCopyPrompt = adCopyPrompts.find((p) => p.is_default) || adCopyPrompts[0] || null;
      setAdCopyPrompt(defaultAdCopyPrompt);
      setAdCopyPromptDraft(defaultAdCopyPrompt?.system_prompt || '');
    } catch (err) { setManageError(getApiErrorMessage(err, 'Failed to load lookup data')); }
    finally { setManageLoading(false); }
  };

  const handleOpenManage = () => {
    setManageOpen(true); setManageTab(0); setManageError('');
    loadManageGlobalLookups(showArchived);
    if (manageVertical) loadManageVerticalLookups(manageVertical, showArchived);
  };
  const handleManageVerticalChange = (v) => { setManageVertical(v); loadManageVerticalLookups(v, showArchived); };
  const handleToggleShowArchived = () => {
    const next = !showArchived; setShowArchived(next);
    loadManageGlobalLookups(next);
    if (manageVertical) loadManageVerticalLookups(manageVertical, next);
  };

  const openEditDialog = (type, item = null) => {
    const nameField = type === 'avatar' ? 'name' : 'label';
    setEditingItem(item
      ? { type, id: item.id, name: item[nameField] || item.name || '', slug: item.slug, vertical: item.vertical }
      : { type, id: null, name: '', slug: '', vertical: ['angle', 'style', 'ad_type'].includes(type) ? null : manageVertical }
    );
    setEditDialogOpen(true); setManageError('');
  };

  const handleSaveItem = async () => {
    if (!editingItem || !editingItem.name.trim() || !editingItem.slug.trim()) return;
    setManageSaving(true); setManageError('');
    try {
      const { type, id, name, slug, vertical } = editingItem;
      if (type === 'avatar') { const p = { name: name.trim(), slug: slug.trim(), vertical }; id ? await updateAvatar(id, p) : await createAvatar(p); }
      else if (type === 'pain_point') { const p = { label: name.trim(), slug: slug.trim(), vertical }; id ? await updatePainPoint(id, p) : await createPainPoint(p); }
      else if (type === 'angle') { const p = { label: name.trim(), slug: slug.trim() }; id ? await updateAngleChoice(id, p) : await createAngleChoice(p); }
      else if (type === 'ad_type') { const p = { label: name.trim(), slug: slug.trim() }; id ? await updateAdTypeChoice(id, p) : await createAdTypeChoice(p); }
      else if (type === 'style') { const p = { label: name.trim(), slug: slug.trim() }; id ? await updateStyleChoice(id, p) : await createStyleChoice(p); }
      else if (type === 'group') { const p = { name: name.trim(), slug: slug.trim(), members: editingItem.members || [] }; id ? await updateDesignerGroup(id, p) : await createDesignerGroup(p); }
      setEditDialogOpen(false); setEditingItem(null);
      if (['angle', 'style', 'ad_type', 'group'].includes(type)) {
        if (type === 'group') { const gR = await listDesignerGroups(); setDesignerGroups(Array.isArray(gR.data) ? gR.data : []); }
        await loadManageGlobalLookups(showArchived);
        const [aR, atR, sR] = await Promise.all([listAngleChoices(), listAdTypeChoices(), listStyleChoices()]);
        setAngles(Array.isArray(aR.data) ? aR.data : []); setAdTypes(Array.isArray(atR.data) ? atR.data : []); setStyles(Array.isArray(sR.data) ? sR.data : []);
      } else { await loadManageVerticalLookups(manageVertical, showArchived); }
    } catch (err) { setManageError(getApiErrorMessage(err, 'Failed to save')); }
    finally { setManageSaving(false); }
  };

  const handleToggleActive = async (type, id, currentActive) => {
    setManageSaving(true); setManageError('');
    try {
      const p = { active: !currentActive };
      if (type === 'avatar') await updateAvatar(id, p);
      else if (type === 'pain_point') await updatePainPoint(id, p);
      else if (type === 'angle') await updateAngleChoice(id, p);
      else if (type === 'ad_type') await updateAdTypeChoice(id, p);
      else if (type === 'style') await updateStyleChoice(id, p);
      if (['angle', 'style', 'ad_type'].includes(type)) {
        await loadManageGlobalLookups(showArchived);
        const [aR, atR, sR] = await Promise.all([listAngleChoices(), listAdTypeChoices(), listStyleChoices()]);
        setAngles(Array.isArray(aR.data) ? aR.data : []); setAdTypes(Array.isArray(atR.data) ? atR.data : []); setStyles(Array.isArray(sR.data) ? sR.data : []);
      } else { await loadManageVerticalLookups(manageVertical, showArchived); }
    } catch (err) { setManageError(getApiErrorMessage(err, 'Failed to update')); }
    finally { setManageSaving(false); }
  };

  const openDesignerDialog = (d = null) => {
    setEditingDesigner(d ? { id: d.id, user: d.user, display_name: d.display_name || '', slug: d.slug || '' } : { id: null, user: '', display_name: '', slug: '' });
    setDesignerDialogOpen(true); setManageError('');
  };
  const handleSaveDesigner = async () => {
    if (!editingDesigner?.user || !editingDesigner?.slug?.trim()) return;
    setManageSaving(true); setManageError('');
    try {
      const p = { user: editingDesigner.user, display_name: editingDesigner.display_name.trim(), slug: editingDesigner.slug.trim() };
      editingDesigner.id ? await updateDesignerProfile(editingDesigner.id, p) : await createDesignerProfile(p);
      setDesignerDialogOpen(false); setEditingDesigner(null);
      await loadManageGlobalLookups(showArchived);
      const res = await listDesignerProfiles(); setDesigners(Array.isArray(res.data) ? res.data : []);
    } catch (err) { setManageError(getApiErrorMessage(err, 'Failed to save designer')); }
    finally { setManageSaving(false); }
  };
  const handleToggleDesignerActive = async (id, active) => {
    setManageSaving(true); setManageError('');
    try {
      await updateDesignerProfile(id, { active: !active });
      await loadManageGlobalLookups(showArchived);
      const res = await listDesignerProfiles(); setDesigners(Array.isArray(res.data) ? res.data : []);
    } catch (err) { setManageError(getApiErrorMessage(err, 'Failed to update')); }
    finally { setManageSaving(false); }
  };

  const handleSavePrompt = async () => {
    if (!scriptPrompt || !scriptPromptDraft.trim()) return;
    setManageSaving(true);
    setManageError('');
    try {
      await updateScriptPrompt(scriptPrompt.id, { system_prompt: scriptPromptDraft });
      setScriptPrompt((prev) => ({ ...prev, system_prompt: scriptPromptDraft }));
    } catch (err) {
      setManageError(getApiErrorMessage(err, 'Failed to save prompt'));
    } finally {
      setManageSaving(false);
    }
  };

  const handleSaveAdCopyPrompt = async () => {
    if (!adCopyPrompt || !adCopyPromptDraft.trim()) return;
    setManageSaving(true);
    setManageError('');
    try {
      await updateAdCopyPrompt(adCopyPrompt.id, { system_prompt: adCopyPromptDraft });
      setAdCopyPrompt((prev) => ({ ...prev, system_prompt: adCopyPromptDraft }));
    } catch (err) {
      setManageError(getApiErrorMessage(err, 'Failed to save ad copy prompt'));
    } finally {
      setManageSaving(false);
    }
  };

  // Helper to render a lookup table for the manage dialog
  const renderLookupTable = (items, type, nameKey = 'label') => (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="flex-end">
        <Button size="small" startIcon={<AddIcon />} onClick={() => openEditDialog(type)}>
          Add {{ avatar: 'Avatar', pain_point: 'Pain Point', angle: 'Angle', ad_type: 'Ad Type', style: 'Style' }[type]}
        </Button>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow><TableCell>{nameKey === 'name' ? 'Name' : 'Label'}</TableCell><TableCell>Slug</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={3} align="center">None found.</TableCell></TableRow>
            ) : items.map((item) => (
              <TableRow key={item.id} hover sx={!item.active ? { opacity: 0.5 } : {}}>
                <TableCell>{item[nameKey]}{!item.active ? <Chip label="Archived" size="small" sx={{ ml: 1, height: 18 }} /> : null}</TableCell>
                <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{item.slug}</Typography></TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEditDialog(type, item)}><EditIcon fontSize="small" /></IconButton>
                  <Button size="small" sx={{ minWidth: 0, ml: 0.5, fontSize: '0.7rem' }} color={item.active ? 'warning' : 'success'} disabled={manageSaving} onClick={() => handleToggleActive(type, item.id, item.active)}>
                    {item.active ? 'Archive' : 'Restore'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );

  return (
    <Box sx={{ px: 2, py: 2 }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700}>Creative Flights</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadFlights} disabled={flightsLoading}>Refresh</Button>
          {isStaff ? (
            <FormControlLabel
              control={<Switch size="small" checked={showArchivedFlights} onChange={(e) => { setShowArchivedFlights(e.target.checked); }} />}
              label={<Typography variant="caption">Archived</Typography>}
            />
          ) : null}
          <Button variant="outlined" color="inherit" startIcon={<ApiIcon />} onClick={() => setApiModalOpen(true)}>API</Button>
          {isStaff ? <Button variant="outlined" startIcon={<SettingsIcon />} onClick={handleOpenManage}>Manage Lookups</Button> : null}
          {isStaff ? <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateFlightOpen(true)}>Create Flight</Button> : null}
        </Stack>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={1.5} mb={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Autocomplete
          size="small" sx={{ minWidth: 200 }}
          options={clientOptions} loading={clientOptionsLoading}
          value={findClientOptionByValue(filterClient) || null}
          onInputChange={(_e, v) => handleClientSearch(v)}
          onChange={(_e, v) => setFilterClient(v?.value || '')}
          renderInput={(params) => <TextField {...params} label="Client" placeholder="All" />}
        />
        <TextField size="small" select label="Status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="in_progress">In Progress</MenuItem>
          <MenuItem value="complete">Complete</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
        {isStaff ? (
          <TextField size="small" select label="Designer" value={filterDesigner} onChange={(e) => setFilterDesigner(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">All</MenuItem>
            {designers.map((d) => <MenuItem key={d.id} value={d.id}>{d.display_name || d.slug}</MenuItem>)}
          </TextField>
        ) : null}
        <Button size="small" variant="outlined" onClick={loadFlights} disabled={flightsLoading}>Apply</Button>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

      {/* Flights list */}
      {flightsLoading ? (
        <Stack alignItems="center" py={6}><CircularProgress /></Stack>
      ) : flights.length === 0 ? (
        <Card variant="outlined"><CardContent><Typography align="center" color="text.secondary">No flights found.</Typography></CardContent></Card>
      ) : (
        <Stack spacing={1}>
          {flights.map((flight) => {
            const progress = flight.target_count > 0 ? Math.round((flight.completed_task_count / flight.target_count) * 100) : 0;
            const isExpanded = expandedFlightId === flight.id;
            const tasks = flightTasks[flight.id] || [];
            const tasksLoading = flightTasksLoading[flight.id] || false;

            return (
              <Accordion key={flight.id} expanded={isExpanded} onChange={() => handleExpandFlight(flight.id)} variant="outlined" disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }} flexWrap="wrap" useFlexGap>
                    <Typography fontWeight={700} sx={{ minWidth: 200, ...(flight.archived ? { opacity: 0.5, textDecoration: 'line-through' } : {}) }}>{flight.flight_code ? `${flight.flight_code} — ` : ''}{flight.name}</Typography>
                    {flight.archived ? <Chip label="Archived" size="small" variant="outlined" sx={{ height: 20 }} /> : null}
                    {flight.client_name ? (
                      <Typography variant="body2" color="text.secondary">{flight.client_name}</Typography>
                    ) : null}
                    <Chip label={flight.status_display} color={FLIGHT_STATUS_COLORS[flight.status] || 'default'} size="small" />
                    <Typography variant="caption" color="text.secondary">
                      {flight.designer_name || 'No designer'} &middot; {flight.start_date} — {flight.end_date}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 150 }}>
                      <LinearProgress variant="determinate" value={progress} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                      <Typography variant="caption" fontWeight={600}>{flight.completed_task_count}/{flight.target_count}</Typography>
                      {flight.approved_task_count > 0 ? (
                        <Chip label={`${flight.approved_task_count} approved`} color="success" size="small" sx={{ height: 20 }} />
                      ) : null}
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Status summary pills */}
                  {tasks.length > 0 ? (() => {
                    const counts = {};
                    tasks.forEach((t) => { counts[t.status] = (counts[t.status] || 0) + 1; });
                    const ORDER = [
                      { key: 'assigned', label: 'Assigned' },
                      { key: 'in_progress', label: 'In Progress' },
                      { key: 'submitted', label: 'Submitted' },
                      { key: 'needs_revision', label: 'Needs Edits' },
                      { key: 'approved', label: 'Approved' },
                      { key: 'draft', label: 'Draft' },
                    ];
                    return (
                      <Stack direction="row" spacing={0.75} mb={1.5} flexWrap="wrap" useFlexGap>
                        {ORDER.filter((s) => counts[s.key]).map((s) => (
                          <Chip key={s.key} label={`${counts[s.key]} ${s.label}`} size="small" sx={{ height: 22, fontWeight: 600, fontSize: '0.7rem', ...(STATUS_CHIP_SX[s.key] || {}) }} />
                        ))}
                        <Chip label={`${tasks.length} Total`} size="small" variant="outlined" sx={{ height: 22, fontWeight: 600, fontSize: '0.7rem' }} />
                      </Stack>
                    );
                  })() : null}
                  {/* Flight actions */}
                  {isStaff ? (
                    <Stack direction="row" spacing={1} mb={2} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => { setGenerateFlightId(flight.id); setGenerateOpen(true); }}>
                        Generate Tasks
                      </Button>
                      {selectedTaskIds.size > 0 && tasks.some((t) => selectedTaskIds.has(t.id)) ? (
                        <Button size="small" variant="outlined" onClick={() => openBulkApply(flight.id)}>
                          Apply to {[...selectedTaskIds].filter((id) => tasks.some((t) => t.id === id)).length} selected
                        </Button>
                      ) : null}
                      <Box sx={{ flex: 1 }} />
                      <Button size="small" color="inherit" startIcon={<DownloadIcon />} onClick={async () => {
                        try {
                          const res = await exportFlightTasks(flight.id);
                          const blob = new Blob([res.data], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${(flight.name || 'flight').replace(/\s+/g, '_')}_tasks.csv`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch (_e) { /* silent */ }
                      }}>
                        Export CSV
                      </Button>
                      <Button size="small" color={flight.archived ? 'primary' : 'inherit'} onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await archiveFlight(flight.id);
                          loadFlights();
                        } catch (_e) { /* silent */ }
                      }}>
                        {flight.archived ? 'Unarchive' : 'Archive'}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} mb={2} justifyContent="flex-end">
                      <Button size="small" color="inherit" startIcon={<DownloadIcon />} onClick={async () => {
                        try {
                          const res = await exportFlightTasks(flight.id);
                          const blob = new Blob([res.data], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${(flight.name || 'flight').replace(/\s+/g, '_')}_tasks.csv`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch (_e) { /* silent */ }
                      }}>
                        Export CSV
                      </Button>
                    </Stack>
                  )}

                  {/* Tasks table */}
                  {tasksLoading ? (
                    <Stack alignItems="center" py={3}><CircularProgress size={24} /></Stack>
                  ) : tasks.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" py={2}>No tasks yet. Generate tasks to get started.</Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 0.75 }, '& .MuiSelect-select': { py: 0.5, fontSize: '0.75rem' }, '& .MuiInputBase-root': { fontSize: '0.75rem' } }}>
                        <TableHead>
                          <TableRow>
                            {isStaff ? (
                              <TableCell sx={{ width: 40 }}>
                                <Checkbox
                                  size="small"
                                  checked={tasks.filter((t) => ['draft', 'assigned'].includes(t.status)).length > 0 && tasks.filter((t) => ['draft', 'assigned'].includes(t.status)).every((t) => selectedTaskIds.has(t.id))}
                                  indeterminate={tasks.filter((t) => ['draft', 'assigned'].includes(t.status)).some((t) => selectedTaskIds.has(t.id)) && !tasks.filter((t) => ['draft', 'assigned'].includes(t.status)).every((t) => selectedTaskIds.has(t.id))}
                                  onChange={() => toggleAllInFlight(flight.id)}
                                />
                              </TableCell>
                            ) : null}
                            <TableCell sx={{ width: 50 }}>Code</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Client</TableCell>
                            <TableCell>Vertical</TableCell>
                            <TableCell>Audience</TableCell>
                            <TableCell>Avatar</TableCell>
                            <TableCell>Pain Point</TableCell>
                            <TableCell>Angle</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Style</TableCell>
                            <TableCell>Designer</TableCell>
                            <TableCell align="right" sx={{ width: 60 }}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tasks.map((task, idx) => {
                            const rowAvatars = avatarsCacheRef.current[task.vertical] || [];
                            const rowPainPoints = painPointsCacheRef.current[task.vertical] || [];
                            const canInlineEdit = isStaff && ['draft', 'assigned'].includes(task.status);

                            // Audience-aware sorting — prioritize relevant avatars/pain points
                            const audiencePriority = {
                              '55+': ['safety', 'care', 'peace', 'lthome', 'comfort', 'prevent', 'protect', 'proud'],
                              'women': ['bmom', 'family', 'fashion', 'org', 'embarr', 'care', 'comfort'],
                              'men': ['garage', 'carent', 'diybout', 'storm', 'insure', 'wkndwar', 'protect'],
                            };
                            const boostSlugs = new Set(audiencePriority[task.audience] || []);
                            const sortByAudience = (items) => boostSlugs.size === 0 ? items : [...items].sort((a, b) => {
                              const aBoost = boostSlugs.has(a.slug) ? 0 : 1;
                              const bBoost = boostSlugs.has(b.slug) ? 0 : 1;
                              return aBoost - bBoost;
                            });
                            const sortedAvatars = sortByAudience(rowAvatars);
                            const sortedPainPoints = (() => {
                              const painPriority = {
                                '55+': ['slip', 'mobility', 'safety', 'leak', 'oldroof', 'cold', 'crack'],
                                'women': ['clean', 'mold', 'embarr', 'clutter', 'morning', 'dated'],
                                'men': ['uglygr', 'crack', 'leak', 'stormdmg', 'nrgloss', 'stain'],
                              };
                              const painBoost = new Set(painPriority[task.audience] || []);
                              return painBoost.size === 0 ? rowPainPoints : [...rowPainPoints].sort((a, b) => {
                                const aB = painBoost.has(a.slug) ? 0 : 1;
                                const bB = painBoost.has(b.slug) ? 0 : 1;
                                return aB - bB;
                              });
                            })();

                            return (
                              <TableRow key={task.id} hover sx={{ ...(!task.is_complete ? { bgcolor: 'action.hover' } : {}), ...(selectedTaskIds.has(task.id) ? { bgcolor: 'primary.50' } : {}) }}>
                                {isStaff ? (
                                  <TableCell>
                                    <Checkbox size="small" checked={selectedTaskIds.has(task.id)} onChange={() => toggleTaskSelection(task.id)} disabled={!['draft', 'assigned'].includes(task.status)} />
                                  </TableCell>
                                ) : null}
                                <TableCell><Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem' }}>{task.task_code || `#${idx + 1}`}</Typography></TableCell>
                                <TableCell>
                                  <Chip label={task.status_display} size="small" sx={{ height: 20, ...(STATUS_CHIP_SX[task.status] || STATUS_CHIP_SX.draft) }} />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>{task.client_name || '—'}</Typography>
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.vertical || ''} onChange={(e) => handleInlineUpdate(task.id, flight.id, 'vertical', e.target.value)} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: !task.vertical }}>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      {getVerticalsForClient(task.client).map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{task.vertical_name || '—'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.audience || 'all'} onChange={(e) => handleInlineUpdate(task.id, flight.id, 'audience', e.target.value)} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: true }}>
                                      {AUDIENCE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{AUDIENCE_OPTIONS.find((o) => o.value === task.audience)?.label || 'All Ages'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.avatar || ''} onChange={(e) => handleInlineUpdate(task.id, flight.id, 'avatar', e.target.value)} disabled={!task.vertical} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: !task.avatar }}>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      {sortedAvatars.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{task.avatar_name || '—'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.pain_point || ''} onChange={(e) => handleInlineUpdate(task.id, flight.id, 'pain_point', e.target.value)} disabled={!task.vertical} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: !task.pain_point }}>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      {sortedPainPoints.map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{task.pain_point_label || '—'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.angle || ''} onChange={(e) => handleInlineUpdate(task.id, flight.id, 'angle', e.target.value)} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: !task.angle }}>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      {angles.map((a) => <MenuItem key={a.id} value={a.id}>{a.label}{a.description ? <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>— {a.description}</Typography> : null}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{task.angle_label || '—'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.ad_type || ''} onChange={(e) => handleInlineUpdate(task.id, flight.id, 'ad_type', e.target.value)} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: !task.ad_type }}>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      {adTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{task.ad_type_label || '—'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.style || ''} onChange={(e) => handleInlineUpdate(task.id, flight.id, 'style', e.target.value)} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: !task.style }}>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      {styles.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}{s.description ? <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>— {s.description}</Typography> : null}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{task.style_label || '—'}</Typography>}
                                </TableCell>
                                <TableCell>
                                  {canInlineEdit ? (
                                    <TextField select variant="standard" fullWidth value={task.designer_group ? `g:${task.designer_group}` : (task.designer || '')} onChange={async (e) => {
                                      const val = e.target.value;
                                      let patch;
                                      if (typeof val === 'string' && val.startsWith('g:')) {
                                        patch = { designer_group: Number(val.slice(2)), designer: null };
                                      } else {
                                        patch = { designer: val || null, designer_group: null };
                                      }
                                      try {
                                        await updateCreativeTask(task.id, patch);
                                        setFlightTasks((prev) => ({ ...prev, [flight.id]: (prev[flight.id] || []).map((t) => t.id === task.id ? { ...t, ...patch } : t) }));
                                      } catch (_e) { /* silent */ }
                                    }} SelectProps={{ displayEmpty: true }} InputProps={{ disableUnderline: !(task.designer || task.designer_group) }}>
                                      <MenuItem value=""><em>—</em></MenuItem>
                                      {designerGroups.length > 0 ? <MenuItem disabled sx={{ opacity: 0.6, fontSize: '0.75rem', py: 0.25 }}>Groups</MenuItem> : null}
                                      {designerGroups.map((g) => <MenuItem key={`g:${g.id}`} value={`g:${g.id}`}>{g.name} ({g.member_count})</MenuItem>)}
                                      {designerGroups.length > 0 && designers.length > 0 ? <MenuItem disabled sx={{ opacity: 0.6, fontSize: '0.75rem', py: 0.25 }}>Individuals</MenuItem> : null}
                                      {designers.map((d) => <MenuItem key={d.id} value={d.id}>{d.display_name || d.slug}</MenuItem>)}
                                    </TextField>
                                  ) : <Typography variant="caption">{task.designer_name || '—'}</Typography>}
                                </TableCell>
                                <TableCell align="right">
                                  {isStaff ? <IconButton size="small" onClick={() => openEditTask(task)} title="Full edit"><EditIcon fontSize="small" /></IconButton> : null}
                                  <IconButton size="small" onClick={() => openDetail(task.id)} title="View"><VisibilityIcon fontSize="small" /></IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}

      {/* ========== CREATE FLIGHT DIALOG ========== */}
      <Dialog open={createFlightOpen} onClose={() => setCreateFlightOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Flight</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {createFlightError ? <Alert severity="error">{createFlightError}</Alert> : null}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="Flight Name" required fullWidth value={flightForm.name} onChange={(e) => setFlightForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Week of March 24" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select fullWidth label="Assign To" value={flightForm.designer_group ? `g:${flightForm.designer_group}` : (flightForm.designer || '')} onChange={(e) => {
                  const val = e.target.value;
                  if (typeof val === 'string' && val.startsWith('g:')) {
                    setFlightForm((p) => ({ ...p, designer_group: Number(val.slice(2)), designer: '' }));
                  } else {
                    setFlightForm((p) => ({ ...p, designer: val, designer_group: '' }));
                  }
                }}>
                  <MenuItem value="">None</MenuItem>
                  {designerGroups.length > 0 ? <MenuItem disabled sx={{ opacity: 0.6, fontSize: '0.75rem', py: 0.25 }}>Groups</MenuItem> : null}
                  {designerGroups.map((g) => <MenuItem key={`g:${g.id}`} value={`g:${g.id}`}>{g.name} ({g.member_count})</MenuItem>)}
                  {designerGroups.length > 0 && designers.length > 0 ? <MenuItem disabled sx={{ opacity: 0.6, fontSize: '0.75rem', py: 0.25 }}>Individuals</MenuItem> : null}
                  {designers.map((d) => <MenuItem key={d.id} value={d.id}>{d.display_name || d.slug}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6} md={3}><TextField label="Start Date" type="date" fullWidth required InputLabelProps={{ shrink: true }} value={flightForm.start_date} onChange={(e) => setFlightForm((p) => ({ ...p, start_date: e.target.value }))} /></Grid>
              <Grid item xs={6} md={3}><TextField label="End Date" type="date" fullWidth required InputLabelProps={{ shrink: true }} value={flightForm.end_date} onChange={(e) => setFlightForm((p) => ({ ...p, end_date: e.target.value }))} /></Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Notes" fullWidth value={flightForm.notes} onChange={(e) => setFlightForm((p) => ({ ...p, notes: e.target.value }))} />
              </Grid>
            </Grid>

            <Divider />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={700}>Ad Segments ({totalSegmentCount(flightSegments)} total ads)</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setFlightSegments((p) => [...p, { client: '', vertical: '', count: 5 }])}>
                Add Segment
              </Button>
            </Stack>

            {flightSegments.map((seg, idx) => (
              <Card key={idx} variant="outlined" sx={{ p: 1.5 }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <Autocomplete
                      size="small"
                      options={clientOptions} loading={clientOptionsLoading}
                      value={findClientOptionByValue(seg.client) || null}
                      onInputChange={(_e, v) => handleClientSearch(v)}
                      onChange={(_e, v) => { const cid = v?.value || ''; if (cid) loadClientVerticals(cid); setFlightSegments((p) => p.map((s, i) => i === idx ? { ...s, client: cid, vertical: '' } : s)); }}
                      renderInput={(params) => <TextField {...params} label="Client" required />}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField size="small" select fullWidth label="Vertical" required value={seg.vertical} onChange={(e) => setFlightSegments((p) => p.map((s, i) => i === idx ? { ...s, vertical: e.target.value } : s))} disabled={!seg.client}>
                      <MenuItem value="">Select vertical</MenuItem>
                      {getVerticalsForClient(seg.client).map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={8} md={2}>
                    <TextField size="small" label="# Ads" type="number" fullWidth required value={seg.count} onChange={(e) => setFlightSegments((p) => p.map((s, i) => i === idx ? { ...s, count: e.target.value } : s))} inputProps={{ min: 1, max: 200 }} />
                  </Grid>
                  <Grid item xs={4} md={1}>
                    {flightSegments.length > 1 ? (
                      <IconButton size="small" color="error" onClick={() => setFlightSegments((p) => p.filter((_, i) => i !== idx))}>
                        <Typography variant="caption" sx={{ fontSize: '1rem' }}>×</Typography>
                      </IconButton>
                    ) : null}
                  </Grid>
                </Grid>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFlightOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFlight} disabled={creatingFlight || !flightForm.name || !flightForm.start_date || !flightForm.end_date || totalSegmentCount(flightSegments) === 0}>
            {creatingFlight ? 'Creating...' : `Create Flight (${totalSegmentCount(flightSegments)} ads)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== GENERATE TASKS DIALOG (add to existing flight) ========== */}
      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Tasks to Flight</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2">Segments ({totalSegmentCount(generateSegments)} tasks)</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setGenerateSegments((p) => [...p, { client: '', vertical: '', count: 5 }])}>
                Add Segment
              </Button>
            </Stack>
            {generateSegments.map((seg, idx) => (
              <Card key={idx} variant="outlined" sx={{ p: 1.5 }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <Autocomplete
                      size="small"
                      options={clientOptions} loading={clientOptionsLoading}
                      value={findClientOptionByValue(seg.client) || null}
                      onInputChange={(_e, v) => handleClientSearch(v)}
                      onChange={(_e, v) => { const cid = v?.value || ''; if (cid) loadClientVerticals(cid); setGenerateSegments((p) => p.map((s, i) => i === idx ? { ...s, client: cid, vertical: '' } : s)); }}
                      renderInput={(params) => <TextField {...params} label="Client" required />}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField size="small" select fullWidth label="Vertical" required value={seg.vertical} onChange={(e) => setGenerateSegments((p) => p.map((s, i) => i === idx ? { ...s, vertical: e.target.value } : s))} disabled={!seg.client}>
                      <MenuItem value="">Select vertical</MenuItem>
                      {getVerticalsForClient(seg.client).map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={8} md={2}>
                    <TextField size="small" label="# Ads" type="number" fullWidth required value={seg.count} onChange={(e) => setGenerateSegments((p) => p.map((s, i) => i === idx ? { ...s, count: e.target.value } : s))} inputProps={{ min: 1, max: 200 }} />
                  </Grid>
                  <Grid item xs={4} md={1}>
                    {generateSegments.length > 1 ? (
                      <IconButton size="small" color="error" onClick={() => setGenerateSegments((p) => p.filter((_, i) => i !== idx))}>
                        <Typography variant="caption" sx={{ fontSize: '1rem' }}>×</Typography>
                      </IconButton>
                    ) : null}
                  </Grid>
                </Grid>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleGenerateTasks} disabled={generatingTasks || totalSegmentCount(generateSegments) === 0}>
            {generatingTasks ? 'Generating...' : `Generate ${totalSegmentCount(generateSegments)} Tasks`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== EDIT TASK DIALOG ========== */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Task</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {editError ? <Alert severity="error">{editError}</Alert> : null}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><TextField select fullWidth label="Vertical" value={editForm.vertical || ''} onChange={(e) => handleEditVerticalChange(e.target.value)}><MenuItem value="">—</MenuItem>{verticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={6}><TextField select fullWidth label="Designer" value={editForm.designer || ''} onChange={(e) => setEditForm((p) => ({ ...p, designer: e.target.value }))}><MenuItem value="">—</MenuItem>{designers.map((d) => <MenuItem key={d.id} value={d.id}>{d.display_name || d.slug}</MenuItem>)}</TextField></Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><TextField select fullWidth label="Avatar" value={editForm.avatar || ''} onChange={(e) => setEditForm((p) => ({ ...p, avatar: e.target.value }))} disabled={!editForm.vertical}><MenuItem value="">—</MenuItem>{editAvatars.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={6}><TextField select fullWidth label="Pain Point" value={editForm.pain_point || ''} onChange={(e) => setEditForm((p) => ({ ...p, pain_point: e.target.value }))} disabled={!editForm.vertical}><MenuItem value="">—</MenuItem>{editPainPoints.map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}</TextField></Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}><TextField select fullWidth label="Angle" value={editForm.angle || ''} onChange={(e) => setEditForm((p) => ({ ...p, angle: e.target.value }))}><MenuItem value="">—</MenuItem>{angles.map((a) => <MenuItem key={a.id} value={a.id}>{a.label}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={4}><TextField select fullWidth label="Ad Type" value={editForm.ad_type || ''} onChange={(e) => setEditForm((p) => ({ ...p, ad_type: e.target.value }))}><MenuItem value="">—</MenuItem>{adTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}</TextField></Grid>
              <Grid item xs={12} md={4}><TextField select fullWidth label="Style" value={editForm.style || ''} onChange={(e) => setEditForm((p) => ({ ...p, style: e.target.value }))}><MenuItem value="">—</MenuItem>{styles.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}</TextField></Grid>
            </Grid>
            <TextField select fullWidth label="Audience" value={editForm.audience || 'all'} onChange={(e) => setEditForm((p) => ({ ...p, audience: e.target.value }))}>
              {AUDIENCE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField label="Hook Description" fullWidth multiline minRows={2} value={editForm.hook_text || ''} onChange={(e) => setEditForm((p) => ({ ...p, hook_text: e.target.value }))} />
            <TextField label="Hook Slug" fullWidth value={editForm.hook_slug || ''} onChange={(e) => setEditForm((p) => ({ ...p, hook_slug: e.target.value.slice(0, 20) }))} helperText={`${(editForm.hook_slug || '').length}/20`} InputProps={{ sx: { fontFamily: 'monospace' } }} />
            {editForm.script_text ? (
              <TextField
                label="Ad Script"
                fullWidth multiline minRows={6}
                value={editForm.script_text || ''}
                onChange={(e) => setEditForm((p) => ({ ...p, script_text: e.target.value }))}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
            ) : null}
            <TextField label="Frame.io URL" fullWidth type="url" value={editForm.frame_io_url || ''} onChange={(e) => setEditForm((p) => ({ ...p, frame_io_url: e.target.value }))} placeholder="https://app.frame.io/..." />
            <TextField label="Notes" multiline minRows={2} fullWidth value={editForm.notes || ''} onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={editing}>{editing ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* ========== DETAIL DIALOG ========== */}
      <Dialog open={detailOpen} onClose={() => { setDetailOpen(false); setDetailTask(null); }} maxWidth="md" fullWidth>
        <DialogTitle>{detailTask?.task_code ? `${detailTask.task_code} — Task Details` : 'Task Details'}</DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detailTask ? (
            <Stack alignItems="center" py={4}><CircularProgress /></Stack>
          ) : (
            <Stack spacing={2.5}>
              {/* Structured name + status header */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="caption" color="text.secondary">Structured Name</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all' }}>{detailTask.structured_name}</Typography>
                </Box>
                <Chip label={detailTask.status_display} sx={STATUS_CHIP_SX[detailTask.status] || STATUS_CHIP_SX.draft} />
                {detailTask.rating ? (
                  <Chip label={`Rating: ${detailTask.rating}/5`} size="small" color={detailTask.rating >= 3 ? 'success' : 'error'} variant="outlined" sx={{ height: 22 }} />
                ) : null}
              </Stack>

              {/* ===== CLIENT CONTEXT ===== */}
              {detailTask.client_context ? (() => {
                const ctx = detailTask.client_context;
                const hasContext = ctx.notes || ctx.website_url || ctx.logo_url || (ctx.offers && ctx.offers.length) || ctx.client_assets || ctx.shared_assets;
                if (!hasContext) return null;
                return (
                  <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2" color="primary">Client Context — {detailTask.client_name}</Typography>

                        {/* Quick links row */}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {ctx.website_url ? (
                            <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={ctx.website_url} target="_blank" rel="noopener noreferrer">
                              Website
                            </Button>
                          ) : null}
                          {ctx.logo_url ? (
                            <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={ctx.logo_url} target="_blank" rel="noopener noreferrer">
                              Logo
                            </Button>
                          ) : null}
                          {ctx.client_assets?.client_asset_folder_url ? (
                            <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={ctx.client_assets.client_asset_folder_url} target="_blank" rel="noopener noreferrer">
                              Client Assets
                            </Button>
                          ) : null}
                          {ctx.shared_assets?.general_asset_folder_url ? (
                            <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={ctx.shared_assets.general_asset_folder_url} target="_blank" rel="noopener noreferrer">
                              Shared Assets
                            </Button>
                          ) : null}
                          {ctx.shared_assets?.recent_ad_examples_url ? (
                            <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={ctx.shared_assets.recent_ad_examples_url} target="_blank" rel="noopener noreferrer">
                              Recent Ads
                            </Button>
                          ) : null}
                          {detailTask.frame_io_url ? (
                            <Button size="small" variant="contained" color="info" startIcon={<OpenInNewIcon />} component="a" href={detailTask.frame_io_url} target="_blank" rel="noopener noreferrer">
                              Frame.io
                            </Button>
                          ) : null}
                        </Stack>

                        {/* Offers */}
                        {ctx.offers && ctx.offers.length ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Offers for {detailTask.vertical_name}</Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              {ctx.offers.map((o) => (
                                <Chip
                                  key={o.id}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  label={`${o.offer_type_display}: ${o.display_label}${o.financing_details ? ` (${o.financing_details})` : ''}`}
                                />
                              ))}
                            </Stack>
                          </Box>
                        ) : null}

                        {/* Brand notes */}
                        {ctx.notes ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Brand Notes</Typography>
                            <Typography variant="body2" sx={{ mt: 0.25, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {ctx.notes}
                            </Typography>
                          </Box>
                        ) : null}

                        {/* AI instructions — hidden from designer view, used internally by AI */}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })() : null}

              {/* Task parameters */}
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Client</Typography><Typography variant="body2" fontWeight={600}>{detailTask.client_name}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Vertical</Typography><Typography variant="body2">{detailTask.vertical_name || '—'}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Designer</Typography><Typography variant="body2">{detailTask.designer_name || '—'}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Avatar</Typography><Typography variant="body2">{detailTask.avatar_name || '—'}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Pain Point</Typography><Typography variant="body2">{detailTask.pain_point_label || '—'}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Angle</Typography><Typography variant="body2">{detailTask.angle_label || '—'}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Ad Type</Typography><Typography variant="body2">{detailTask.ad_type_label || '—'}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Style</Typography><Typography variant="body2">{detailTask.style_label || '—'}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Audience</Typography><Typography variant="body2">{AUDIENCE_OPTIONS.find((o) => o.value === detailTask.audience)?.label || 'All Ages'}</Typography></Grid>
              </Grid>

              {/* Hook */}
              {detailTask.hook_text ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={9}><Typography variant="caption" color="text.secondary">Hook</Typography><Typography variant="body2">{detailTask.hook_text}</Typography></Grid>
                  <Grid item xs={12} md={3}><Typography variant="caption" color="text.secondary">Hook Slug</Typography><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{detailTask.hook_slug || '—'}</Typography></Grid>
                </Grid>
              ) : null}

              {detailTask.notes ? <Box><Typography variant="caption" color="text.secondary">Task Notes</Typography><Typography variant="body2">{detailTask.notes}</Typography></Box> : null}

              {/* Frame.io link — editable by staff and designers */}
              <TextField
                size="small" fullWidth
                label="Frame.io Link"
                type="url"
                value={detailTask.frame_io_url || ''}
                placeholder="Paste Frame.io link here..."
                onChange={(e) => setDetailTask((prev) => ({ ...prev, frame_io_url: e.target.value }))}
                onBlur={async (e) => {
                  const url = e.target.value.trim();
                  if (url !== (detailTask._saved_frame_io_url || '')) {
                    try {
                      await updateCreativeTask(detailTask.id, { frame_io_url: url });
                      setDetailTask((prev) => ({ ...prev, _saved_frame_io_url: url }));
                    } catch (_err) { /* silent */ }
                  }
                }}
                  InputProps={{
                    endAdornment: detailTask.frame_io_url ? (
                      <IconButton size="small" component="a" href={detailTask.frame_io_url} target="_blank" rel="noopener noreferrer" title="Open in Frame.io">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    ) : null,
                  }}
                />

              {/* Script */}
              <Divider />
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2">Ad Script</Typography>
                {isStaff && detailTask.vertical && detailTask.avatar && detailTask.pain_point && detailTask.angle && detailTask.ad_type && detailTask.style ? (
                  <Button size="small" startIcon={<AutoFixHighIcon />} disabled={scriptGenerating} onClick={() => handleGenerateScript(detailTask.id)}>
                    {scriptGenerating ? 'Generating...' : (detailTask.script_text ? 'Regenerate Script' : 'Generate Script')}
                  </Button>
                ) : null}
              </Stack>
              {detailTask.script_text ? (
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    {detailTask.script_text.split(/\n\n+/).map((block, i) => {
                      const trimmed = block.trim();
                      if (!trimmed) return null;
                      // Detect labeled sections like "HOOK:", "BODY:", etc.
                      const labelMatch = trimmed.match(/^(HOOK_SLUG|HOOK|BODY|OFFER|CTA|SCRIPT)[^:]*:\s*(.*)/s);
                      if (labelMatch) {
                        return (
                          <Box key={i}>
                            <Typography variant="caption" color="primary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>{labelMatch[1]}</Typography>
                            <Typography variant="body2" sx={{ mt: 0.25 }}>{labelMatch[2].trim()}</Typography>
                          </Box>
                        );
                      }
                      return <Typography key={i} variant="body2">{trimmed}</Typography>;
                    })}
                  </Stack>
                </Card>
              ) : (
                <Typography variant="body2" color="text.secondary">No script yet. {detailTask.vertical && detailTask.avatar && detailTask.pain_point && detailTask.angle ? 'Click "Generate Script" to create one — it will also generate the hook.' : 'Fill in vertical, avatar, pain point, and angle first.'}</Typography>
              )}

              <Divider />

              {/* Workflow actions */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {isStaff ? <Button variant="outlined" startIcon={<EditIcon />} size="small" onClick={() => openEditTask(detailTask)}>Edit</Button> : null}
                {isStaff && detailTask.status === 'draft' && detailTask.is_complete ? (
                  <Button variant="contained" color="primary" startIcon={<PersonAddIcon />} size="small" disabled={Boolean(actionLoading)} onClick={() => handleAction(assignCreativeTask, detailTask.id, 'assign')}>
                    Assign to Designer
                  </Button>
                ) : null}
                {isStaff && detailTask.status === 'draft' && !detailTask.is_complete ? (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>Fill in all fields and generate a script before assigning.</Typography>
                ) : null}
                {['assigned', 'needs_revision'].includes(detailTask.status) ? <Button variant="contained" startIcon={<PlayArrowIcon />} size="small" disabled={Boolean(actionLoading)} onClick={() => handleAction(startCreativeTask, detailTask.id, 'start')}>Start Work</Button> : null}
                {['in_progress', 'needs_revision', 'assigned'].includes(detailTask.status) ? <Button variant="contained" startIcon={<SendIcon />} size="small" disabled={Boolean(actionLoading)} onClick={() => setSubmitOpen(true)}>Submit for Review</Button> : null}
                {isStaff && ['submitted', 'needs_revision', 'in_progress'].includes(detailTask.status) ? (
                  <>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Chip
                          key={n} label={n} size="small"
                          variant={approveRating === n ? 'filled' : 'outlined'}
                          color={approveRating === n ? (n >= 3 ? 'success' : 'error') : 'default'}
                          onClick={() => setApproveRating(approveRating === n ? 0 : n)}
                          sx={{ minWidth: 28, height: 24, cursor: 'pointer' }}
                        />
                      ))}
                      <Typography variant="caption" color="text.secondary">Rating</Typography>
                    </Stack>
                    <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} size="small" disabled={Boolean(actionLoading)} onClick={async () => {
                      setActionLoading('approve');
                      try {
                        await approveCreativeTask(detailTask.id, approveRating ? { rating: approveRating } : {});
                        const res = await fetchCreativeTask(detailTask.id);
                        setDetailTask(res.data);
                        const flightId = detailTask.flight;
                        if (flightId && flightTasks[flightId]) {
                          setFlightTasks((prev) => ({ ...prev, [flightId]: prev[flightId].map((t) => t.id === detailTask.id ? { ...t, ...res.data } : t) }));
                        }
                      } catch (err) { setError(getApiErrorMessage(err, 'Approve failed')); }
                      setActionLoading('');
                    }}>Approve</Button>
                  </>
                ) : null}
                {isStaff && ['submitted', 'approved', 'in_progress'].includes(detailTask.status) ? (
                  <Button variant="outlined" color="warning" startIcon={<UndoIcon />} size="small" disabled={Boolean(actionLoading)} onClick={() => setRevisionOpen(true)}>Request Revision</Button>
                ) : null}
                {isStaff && detailTask.status === 'approved' ? (
                  <Button variant="contained" color="info" size="small" disabled={Boolean(actionLoading)} onClick={async () => {
                    setFbLaunchTask(detailTask);
                    setFbLaunchResult(null);
                    setFbHeadlineOptions([]);
                    // Pre-fill defaults
                    setFbLaunchForm((prev) => ({
                      ...prev,
                      ad_account_id: '', campaign_id: '', ad_set_ids: [],
                      headline: detailTask.hook_text || '',
                      primary_text: detailTask.script_text || '',
                      description: '',
                      cta_type: 'LEARN_MORE',
                      destination_url: '',
                      display_link: '',
                      page_id: '',
                      page_name: '',
                    }));
                    setFbLaunchOpen(true);
                    setFbShowInactive(false);
                    // Load FB structure (active only) + prefill in parallel
                    if (detailTask.client) {
                      setFbStructureLoading(true);
                      try {
                        const [structRes, prefillRes] = await Promise.all([
                          fetchFacebookAdStructure(detailTask.client, false),
                          fetchLaunchPrefill(detailTask.id),
                        ]);
                        setFbStructure(structRes?.data || []);
                        const pf = prefillRes?.data || {};
                        setFbLaunchForm((prev) => ({
                          ...prev,
                          destination_url: pf.destination_url || prev.destination_url,
                          display_link: pf.display_link || prev.display_link,
                          page_id: pf.page_id || prev.page_id,
                          page_name: pf.page_name || '',
                        }));
                      } catch (_e) { setFbStructure([]); }
                      setFbStructureLoading(false);
                    }
                  }}>
                    {detailTask.facebook_ad_id ? 'Relaunch to Facebook' : 'Launch to Facebook'}
                  </Button>
                ) : null}
                {detailTask.facebook_ad_id ? (
                  <Chip label={`FB Ad: ${detailTask.facebook_ad_id}`} size="small" color="info" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                ) : null}
              </Stack>

              {submitOpen ? (
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2">Submit for Review</Typography>
                    {!detailTask.frame_io_url ? (
                      <Alert severity="warning" variant="outlined">Frame.io link is required before submitting. Paste it above.</Alert>
                    ) : null}
                    <TextField multiline minRows={2} fullWidth placeholder="Add a note about this submission (optional)..." value={submitComment} onChange={(e) => setSubmitComment(e.target.value)} />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => { setSubmitOpen(false); setSubmitComment(''); }}>Cancel</Button>
                      <Button size="small" variant="contained" disabled={!detailTask.frame_io_url || actionLoading === 'submit'} onClick={handleSubmitTask}>
                        {actionLoading === 'submit' ? 'Submitting...' : 'Submit'}
                      </Button>
                    </Stack>
                  </Stack>
                </Card>
              ) : null}

              {revisionOpen ? (
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2">Request Revision</Typography>
                    <TextField multiline minRows={2} fullWidth placeholder="What needs to change?" value={revisionText} onChange={(e) => setRevisionText(e.target.value)} />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => { setRevisionOpen(false); setRevisionText(''); }}>Cancel</Button>
                      <Button size="small" variant="contained" color="warning" disabled={!revisionText.trim() || actionLoading === 'revision'} onClick={handleRequestRevision}>Send</Button>
                    </Stack>
                  </Stack>
                </Card>
              ) : null}

              <Divider />
              <Typography variant="subtitle2">Files ({(detailTask.files || []).length})</Typography>
              {(detailTask.files || []).map((file) => (
                <Stack key={file.id} direction="row" spacing={1} alignItems="center">
                  <Chip label={file.file_type} size="small" variant="outlined" />
                  <Typography variant="body2" component="a" href={file.file} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', textDecoration: 'underline' }}>{file.original_name || 'Download'}</Typography>
                  <Typography variant="caption" color="text.secondary">by {file.uploaded_by_name} &middot; {new Date(file.created_at).toLocaleString()}</Typography>
                </Stack>
              ))}
              <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} size="small" disabled={Boolean(actionLoading)} sx={{ alignSelf: 'flex-start' }}>
                {actionLoading === 'upload' ? 'Uploading...' : 'Upload File'}<input type="file" hidden onChange={handleFileUpload} />
              </Button>

              <Divider />
              <Typography variant="subtitle2">Comments ({(detailTask.comments || []).length})</Typography>
              {(detailTask.comments || []).map((c) => (
                <Card key={c.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" fontWeight={700}>{c.author_name || c.author_email}</Typography>
                      <Typography variant="caption" color="text.secondary">{new Date(c.created_at).toLocaleString()}</Typography>
                    </Stack>
                    <Typography variant="body2">{c.body}</Typography>
                  </Stack>
                </Card>
              ))}
              <Stack direction="row" spacing={1}>
                <TextField fullWidth size="small" placeholder="Add a comment..." value={commentText} onChange={(e) => setCommentText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }} />
                <Button variant="contained" size="small" disabled={!commentText.trim() || commenting} onClick={handleAddComment}>Send</Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => { setDetailOpen(false); setDetailTask(null); }}>Close</Button></DialogActions>
      </Dialog>

      {/* ========== BULK APPLY DIALOG ========== */}
      <Dialog open={bulkApplyOpen} onClose={() => setBulkApplyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Apply to {selectedTaskIds.size} Selected Tasks</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Only set the fields you want to change. Empty fields will be left as-is.
            </Typography>
            <TextField select fullWidth label="Vertical" value={bulkForm.vertical} onChange={(e) => handleBulkVerticalChange(e.target.value)}>
              <MenuItem value="">— Don't change —</MenuItem>
              {verticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField select fullWidth label="Avatar" value={bulkForm.avatar} onChange={(e) => setBulkForm((p) => ({ ...p, avatar: e.target.value }))} disabled={!bulkForm.vertical}>
                  <MenuItem value="">— Don't change —</MenuItem>
                  {bulkAvatars.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField select fullWidth label="Pain Point" value={bulkForm.pain_point} onChange={(e) => setBulkForm((p) => ({ ...p, pain_point: e.target.value }))} disabled={!bulkForm.vertical}>
                  <MenuItem value="">— Don't change —</MenuItem>
                  {bulkPainPoints.map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField select fullWidth label="Angle" value={bulkForm.angle} onChange={(e) => setBulkForm((p) => ({ ...p, angle: e.target.value }))}>
                  <MenuItem value="">— Don't change —</MenuItem>
                  {angles.map((a) => <MenuItem key={a.id} value={a.id}>{a.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField select fullWidth label="Ad Type" value={bulkForm.ad_type} onChange={(e) => setBulkForm((p) => ({ ...p, ad_type: e.target.value }))}>
                  <MenuItem value="">— Don't change —</MenuItem>
                  {adTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField select fullWidth label="Style" value={bulkForm.style} onChange={(e) => setBulkForm((p) => ({ ...p, style: e.target.value }))}>
                  <MenuItem value="">— Don't change —</MenuItem>
                  {styles.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <TextField select fullWidth label="Audience" value={bulkForm.audience} onChange={(e) => setBulkForm((p) => ({ ...p, audience: e.target.value }))}>
              <MenuItem value="">— Don't change —</MenuItem>
              {AUDIENCE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Designer" value={bulkForm.designer} onChange={(e) => setBulkForm((p) => ({ ...p, designer: e.target.value }))}>
              <MenuItem value="">— Don't change —</MenuItem>
              {designers.map((d) => <MenuItem key={d.id} value={d.id}>{d.display_name || d.slug}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkApplyOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkApply} disabled={bulkApplying || !Object.values(bulkForm).some(Boolean)}>
            {bulkApplying ? 'Applying...' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== MANAGE LOOKUPS DIALOG ========== */}
      <Dialog open={manageOpen} onClose={() => setManageOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manage Lookups</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {manageError ? <Alert severity="error" sx={{ mb: 1 }} onClose={() => setManageError('')}>{manageError}</Alert> : null}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Tabs value={manageTab} onChange={(_e, v) => setManageTab(v)} variant="scrollable" scrollButtons="auto" sx={{ flex: 1 }}>
                <Tab label={`Avatars (${manageAvatars.length})`} />
                <Tab label={`Pain Points (${managePainPoints.length})`} />
                <Tab label={`Angles (${manageAngles.length})`} />
                <Tab label={`Ad Types (${manageAdTypes.length})`} />
                <Tab label={`Styles (${manageStyles.length})`} />
                <Tab label={`Designers (${manageDesigners.length})`} />
                <Tab label={`Groups (${manageGroups.length})`} />
                <Tab label="Script Prompt" />
                <Tab label="Ad Copy Prompt" />
              </Tabs>
              <FormControlLabel control={<Switch size="small" checked={showArchived} onChange={handleToggleShowArchived} />} label={<Typography variant="caption">Archived</Typography>} sx={{ ml: 1, mr: 0 }} />
            </Stack>

            {manageTab <= 1 ? (
              <>
                <TextField select fullWidth label="Vertical" value={manageVertical} onChange={(e) => handleManageVerticalChange(e.target.value)}>
                  <MenuItem value="">Select a vertical</MenuItem>
                  {verticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>)}
                </TextField>
                {!manageVertical ? <Typography variant="body2" color="text.secondary">Select a vertical first.</Typography>
                  : manageLoading ? <Stack alignItems="center" py={3}><CircularProgress /></Stack>
                  : manageTab === 0 ? renderLookupTable(manageAvatars, 'avatar', 'name')
                  : renderLookupTable(managePainPoints, 'pain_point', 'label')}
              </>
            ) : null}
            {manageTab === 2 ? (manageLoading ? <Stack alignItems="center" py={3}><CircularProgress /></Stack> : renderLookupTable(manageAngles, 'angle')) : null}
            {manageTab === 3 ? (manageLoading ? <Stack alignItems="center" py={3}><CircularProgress /></Stack> : renderLookupTable(manageAdTypes, 'ad_type')) : null}
            {manageTab === 4 ? (manageLoading ? <Stack alignItems="center" py={3}><CircularProgress /></Stack> : renderLookupTable(manageStyles, 'style')) : null}
            {manageTab === 5 ? (
              manageLoading ? <Stack alignItems="center" py={3}><CircularProgress /></Stack> : (
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="flex-end"><Button size="small" startIcon={<AddIcon />} onClick={() => openDesignerDialog()}>Add Designer</Button></Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Slug</TableCell><TableCell>Email</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                      <TableBody>
                        {manageDesigners.length === 0 ? <TableRow><TableCell colSpan={4} align="center">None.</TableCell></TableRow> : manageDesigners.map((d) => (
                          <TableRow key={d.id} hover sx={!d.active ? { opacity: 0.5 } : {}}>
                            <TableCell>{d.display_name || d.slug}{!d.active ? <Chip label="Archived" size="small" sx={{ ml: 1, height: 18 }} /> : null}</TableCell>
                            <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{d.slug}</Typography></TableCell>
                            <TableCell><Typography variant="caption">{d.user_email}</Typography></TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => openDesignerDialog(d)}><EditIcon fontSize="small" /></IconButton>
                              <Button size="small" sx={{ minWidth: 0, ml: 0.5, fontSize: '0.7rem' }} color={d.active ? 'warning' : 'success'} disabled={manageSaving} onClick={() => handleToggleDesignerActive(d.id, d.active)}>{d.active ? 'Archive' : 'Restore'}</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              )
            ) : null}

            {/* AI Prompt tab */}
            {/* Groups tab */}
            {manageTab === 6 ? (
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="flex-end">
                  <Button size="small" startIcon={<AddIcon />} onClick={() => {
                    setEditingItem({ type: 'group', name: '', slug: '', members: [] });
                    setEditDialogOpen(true);
                  }}>Add Group</Button>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Slug</TableCell><TableCell>Members</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                    <TableBody>
                      {manageGroups.length === 0 ? <TableRow><TableCell colSpan={4} align="center">No groups yet.</TableCell></TableRow> : manageGroups.map((g) => (
                        <TableRow key={g.id}>
                          <TableCell>{g.name}</TableCell>
                          <TableCell><Typography variant="caption" fontFamily="monospace">{g.slug}</Typography></TableCell>
                          <TableCell>{(g.member_names || []).join(', ') || '—'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => {
                              setEditingItem({ type: 'group', id: g.id, name: g.name, slug: g.slug, members: g.members || [] });
                              setEditDialogOpen(true);
                            }}><EditIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            ) : null}

            {manageTab === 7 ? (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  This is the exact system prompt sent to Claude when generating scripts. Edit it to change tone, add rules, adjust output format, etc. Task context (client, vertical, avatar, offers) is automatically appended.
                </Typography>
                <TextField
                  multiline fullWidth minRows={12} maxRows={24}
                  value={scriptPromptDraft}
                  onChange={(e) => setScriptPromptDraft(e.target.value)}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" onClick={() => setScriptPromptDraft(scriptPrompt?.system_prompt || '')} disabled={scriptPromptDraft === (scriptPrompt?.system_prompt || '')}>
                    Reset
                  </Button>
                  <Button size="small" variant="contained" onClick={handleSavePrompt} disabled={manageSaving || !scriptPromptDraft.trim() || scriptPromptDraft === (scriptPrompt?.system_prompt || '')}>
                    {manageSaving ? 'Saving...' : 'Save Prompt'}
                  </Button>
                </Stack>
              </Stack>
            ) : null}

            {manageTab === 8 ? (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  This is the system prompt sent to Claude when generating Facebook ad copy (headline, primary text, description). Task context, script, and client offers are automatically appended.
                </Typography>
                <TextField
                  multiline fullWidth minRows={12} maxRows={24}
                  value={adCopyPromptDraft}
                  onChange={(e) => setAdCopyPromptDraft(e.target.value)}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" onClick={() => setAdCopyPromptDraft(adCopyPrompt?.system_prompt || '')} disabled={adCopyPromptDraft === (adCopyPrompt?.system_prompt || '')}>
                    Reset
                  </Button>
                  <Button size="small" variant="contained" onClick={handleSaveAdCopyPrompt} disabled={manageSaving || !adCopyPromptDraft.trim() || adCopyPromptDraft === (adCopyPrompt?.system_prompt || '')}>
                    {manageSaving ? 'Saving...' : 'Save Prompt'}
                  </Button>
                </Stack>
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setManageOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* ========== ADD/EDIT LOOKUP ITEM DIALOG ========== */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem?.id ? 'Edit' : 'Add'} {{ avatar: 'Avatar', pain_point: 'Pain Point', angle: 'Angle', ad_type: 'Ad Type', style: 'Style', group: 'Designer Group' }[editingItem?.type] || 'Item'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {manageError ? <Alert severity="error">{manageError}</Alert> : null}
            <TextField label={editingItem?.type === 'avatar' ? 'Name' : editingItem?.type === 'group' ? 'Group Name' : 'Label'} fullWidth required value={editingItem?.name || ''} onChange={(e) => {
              const name = e.target.value;
              const max = ['angle', 'style', 'ad_type'].includes(editingItem?.type) ? 10 : 20;
              setEditingItem((p) => ({ ...p, name, ...(!p?.id ? { slug: autoSlug(name).slice(0, max) } : {}) }));
            }} />
            <TextField label="Slug" fullWidth required value={editingItem?.slug || ''} onChange={(e) => {
              const max = ['angle', 'style', 'ad_type'].includes(editingItem?.type) ? 10 : 20;
              setEditingItem((p) => ({ ...p, slug: e.target.value.slice(0, max) }));
            }} helperText={(() => { const max = ['angle', 'style', 'ad_type'].includes(editingItem?.type) ? 10 : 20; return `${(editingItem?.slug || '').length}/${max}`; })()} InputProps={{ sx: { fontFamily: 'monospace' } }} />
            {editingItem?.type === 'group' ? (
              <Autocomplete
                multiple
                options={manageDesigners}
                getOptionLabel={(o) => o.display_name || o.slug}
                value={manageDesigners.filter((d) => (editingItem.members || []).includes(d.id))}
                onChange={(_e, v) => setEditingItem((p) => ({ ...p, members: v.map((d) => d.id) }))}
                renderInput={(params) => <TextField {...params} label="Members" placeholder="Add designers..." />}
                renderTags={(value, getTagProps) => value.map((option, index) => <Chip {...getTagProps({ index })} key={option.id} label={option.display_name || option.slug} size="small" />)}
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveItem} disabled={manageSaving || !editingItem?.name?.trim() || !editingItem?.slug?.trim()}>{manageSaving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* ========== ADD/EDIT DESIGNER DIALOG ========== */}
      <Dialog open={designerDialogOpen} onClose={() => setDesignerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDesigner?.id ? 'Edit' : 'Add'} Designer</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {manageError ? <Alert severity="error">{manageError}</Alert> : null}
            <Autocomplete
              options={staffUsers}
              getOptionLabel={(o) => { const n = `${o.first_name || ''} ${o.last_name || ''}`.trim(); return n ? `${n} (${o.email})` : o.email || ''; }}
              value={staffUsers.find((u) => u.id === editingDesigner?.user) || null}
              onChange={(_e, v) => {
                const name = v ? `${v.first_name || ''} ${v.last_name || ''}`.trim() : '';
                setEditingDesigner((p) => ({ ...p, user: v?.id || '', display_name: p?.display_name || name, ...(!p?.id && !p?.slug ? { slug: autoSlug(v?.first_name || v?.email?.split('@')[0] || '') } : {}) }));
              }}
              disabled={Boolean(editingDesigner?.id)}
              renderInput={(params) => <TextField {...params} label="User" required helperText={editingDesigner?.id ? 'Cannot change after creation' : ''} />}
            />
            <TextField label="Display Name" fullWidth value={editingDesigner?.display_name || ''} onChange={(e) => setEditingDesigner((p) => ({ ...p, display_name: e.target.value }))} />
            <TextField label="Slug" fullWidth required value={editingDesigner?.slug || ''} onChange={(e) => setEditingDesigner((p) => ({ ...p, slug: e.target.value.slice(0, 20) }))} helperText={`cr= component. ${(editingDesigner?.slug || '').length}/20`} InputProps={{ sx: { fontFamily: 'monospace' } }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDesignerDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDesigner} disabled={manageSaving || !editingDesigner?.user || !editingDesigner?.slug?.trim()}>{manageSaving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* ========== LAUNCH TO FACEBOOK MODAL ========== */}
      <Dialog open={fbLaunchOpen} onClose={() => { setFbLaunchOpen(false); setFbLaunchResult(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Launch to Facebook{fbLaunchTask?.task_code ? ` — ${fbLaunchTask.task_code}` : ''}</DialogTitle>

        {/* Result banner — outside DialogContent so it's always visible */}
        {fbLaunchResult?.success ? (
          <Alert severity="success" sx={{ mx: 3, mb: 0, '& .MuiAlert-message': { width: '100%' } }}>
            <Stack spacing={1}>
              <Typography variant="body2" fontWeight={600}>
                {fbLaunchResult.ad_count > 1
                  ? `${fbLaunchResult.ad_count} ads created successfully! (status: PAUSED)`
                  : 'Ad created successfully! (status: PAUSED)'}
              </Typography>
              <Typography variant="body2">Ad Name: <strong>{fbLaunchResult.ad_name}</strong></Typography>
              {fbLaunchResult.facebook_creative_id ? <Typography variant="body2">Creative ID: {fbLaunchResult.facebook_creative_id}</Typography> : null}
              {(fbLaunchResult.ads || []).map((ad) => (
                <Typography key={ad.ad_id} variant="body2">
                  {ad.ad_set_name}: <strong>{ad.ad_id}</strong>
                </Typography>
              ))}
              <Button
                size="small" variant="outlined" color="success"
                component="a"
                href={`https://www.facebook.com/adsmanager/manage/ads?act=${fbLaunchForm.ad_account_id}&selected_adset_ids=${(fbLaunchResult.ads || []).map((a) => a.ad_set_id).join(',')}`}
                target="_blank" rel="noopener noreferrer"
                startIcon={<OpenInNewIcon />}
                sx={{ alignSelf: 'flex-start', mt: 0.5 }}
              >
                View in Ads Manager
              </Button>
            </Stack>
          </Alert>
        ) : fbLaunchResult?.error ? (
          <Alert severity="error" sx={{ mx: 3, mb: 0 }}>
            <Typography variant="body2" fontWeight={600}>Launch failed</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{fbLaunchResult.error}</Typography>
          </Alert>
        ) : null}

        <DialogContent dividers>
          <Stack spacing={2.5}>
            {fbLaunchResult?.dry_run ? (
              <Alert severity="info">Preview — no ad was created. Review the details below and click "Launch" to push to Facebook.</Alert>
            ) : null}

            {/* Creative asset preview — show at top */}
            {fbLaunchTask && (fbLaunchTask.files || []).length > 0 ? (
              <>
                <Typography variant="subtitle2">Creative Assets</Typography>
                <Stack spacing={1}>
                  {(fbLaunchTask.files || []).map((file) => {
                    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.original_name || file.file || '');
                    const isVideo = /\.(mp4|mov|webm|avi)$/i.test(file.original_name || file.file || '');
                    return (
                      <Card key={file.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          {isImage ? (
                            <Box component="img" src={file.file} alt={file.original_name} sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1 }} />
                          ) : isVideo ? (
                            <Box component="video" src={file.file} controls sx={{ width: 200, maxHeight: 120, borderRadius: 1 }} />
                          ) : null}
                          <Stack spacing={0.25} sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>{file.original_name || 'File'}</Typography>
                            <Chip label={file.file_type} size="small" variant="outlined" sx={{ alignSelf: 'flex-start', height: 18 }} />
                          </Stack>
                          <Button size="small" component="a" href={file.file} target="_blank" rel="noopener noreferrer">View</Button>
                        </Stack>
                      </Card>
                    );
                  })}
                </Stack>
                <Typography variant="caption" color="text.secondary">The first deliverable file (video preferred) will be uploaded to Facebook with the ad.</Typography>
                <Divider />
              </>
            ) : null}

            {/* Account / Campaign / Ad Set selectors */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">Facebook Placement</Typography>
              {fbStructureLoading ? <CircularProgress size={16} /> : null}
              <Button size="small" variant={fbShowInactive ? 'contained' : 'outlined'} color="inherit" sx={{ ml: 'auto', fontSize: '0.7rem', py: 0.25 }} disabled={fbStructureLoading} onClick={async () => {
                const next = !fbShowInactive;
                setFbShowInactive(next);
                setFbStructureLoading(true);
                setFbLaunchForm((prev) => ({ ...prev, ad_account_id: '', campaign_id: '', ad_set_id: '' }));
                try {
                  const res = await fetchFacebookAdStructure(fbLaunchTask.client, next);
                  setFbStructure(res?.data || []);
                } catch (_e) { setFbStructure([]); }
                setFbStructureLoading(false);
              }}>
                {fbShowInactive ? 'Showing Inactive' : 'Include Inactive'}
              </Button>
            </Stack>
            <Stack spacing={2}>
              <TextField select label="Ad Account" fullWidth value={fbLaunchForm.ad_account_id} disabled={fbStructureLoading} onChange={(e) => {
                setFbLaunchForm((prev) => ({ ...prev, ad_account_id: e.target.value, campaign_id: '', ad_set_ids: [] }));
              }}>
                {fbStructure.map((acct) => (
                  <MenuItem key={acct.account_id} value={acct.account_id}>{acct.account_name} ({acct.account_id})</MenuItem>
                ))}
              </TextField>
              {(() => {
                const acct = fbStructure.find((a) => a.account_id === fbLaunchForm.ad_account_id);
                const campaigns = acct?.campaigns || [];
                return (
                  <>
                    <TextField select label="Campaign" fullWidth value={fbLaunchForm.campaign_id} disabled={fbStructureLoading || !campaigns.length} onChange={(e) => {
                      setFbLaunchForm((prev) => ({ ...prev, campaign_id: e.target.value, ad_set_ids: [] }));
                    }}>
                      {campaigns.map((c) => (
                        <MenuItem key={c.campaign_id} value={c.campaign_id}>{c.campaign_name} ({c.status})</MenuItem>
                      ))}
                    </TextField>
                    {/* Ad Sets — multi-select with checkboxes */}
                    {(() => {
                      const adSets = campaigns.find((c) => c.campaign_id === fbLaunchForm.campaign_id)?.ad_sets || [];
                      if (!fbLaunchForm.campaign_id) return <TextField label="Ad Sets" fullWidth disabled placeholder="Select a campaign first" />;
                      if (!adSets.length) return <Typography variant="caption" color="text.secondary">No ad sets found in this campaign.</Typography>;
                      return (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Select ad sets to launch into ({fbLaunchForm.ad_set_ids.length} selected):</Typography>
                          <Stack spacing={0.5} sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                            {adSets.map((as) => (
                              <FormControlLabel
                                key={as.ad_set_id}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={fbLaunchForm.ad_set_ids.includes(as.ad_set_id)}
                                    onChange={(e) => {
                                      setFbLaunchForm((prev) => ({
                                        ...prev,
                                        ad_set_ids: e.target.checked
                                          ? [...prev.ad_set_ids, as.ad_set_id]
                                          : prev.ad_set_ids.filter((id) => id !== as.ad_set_id),
                                      }));
                                    }}
                                  />
                                }
                                label={
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2">{as.ad_set_name}</Typography>
                                    <Chip label={as.active ? 'Active' : 'Inactive'} size="small" sx={{ height: 18, fontSize: '0.65rem', ...(as.active ? { bgcolor: '#c8e6c9', color: '#2e7d32' } : { bgcolor: 'grey.200', color: 'grey.600' }) }} />
                                  </Stack>
                                }
                                sx={{ m: 0, '& .MuiFormControlLabel-label': { flex: 1 } }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      );
                    })()}
                  </>
                );
              })()}
            </Stack>

            <Divider />

            {/* Ad copy fields */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">Ad Copy</Typography>
              <Button size="small" variant="outlined" startIcon={<AutoFixHighIcon />} disabled={fbCopyGenerating} onClick={async () => {
                setFbCopyGenerating(true);
                setFbHeadlineOptions([]);
                try {
                  const res = await generateAdCopy(fbLaunchTask.id);
                  const d = res?.data || {};
                  const headlines = d.headlines || (d.headline ? [d.headline] : []);
                  setFbHeadlineOptions(headlines);
                  setFbLaunchForm((prev) => ({
                    ...prev,
                    headline: headlines[0] || prev.headline,
                    primary_text: d.primary_text || prev.primary_text,
                    description: d.description || prev.description,
                  }));
                } catch (err) { setError(getApiErrorMessage(err, 'Ad copy generation failed')); }
                setFbCopyGenerating(false);
              }}>
                {fbCopyGenerating ? 'Generating...' : 'Generate with AI'}
              </Button>
            </Stack>

            {/* Headline picker */}
            {fbHeadlineOptions.length > 1 ? (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Pick a headline:</Typography>
                <Stack spacing={0.5}>
                  {fbHeadlineOptions.map((h, i) => (
                    <Chip
                      key={i}
                      label={h}
                      size="small"
                      variant={fbLaunchForm.headline === h ? 'filled' : 'outlined'}
                      color={fbLaunchForm.headline === h ? 'primary' : 'default'}
                      onClick={() => setFbLaunchForm((prev) => ({ ...prev, headline: h }))}
                      sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                    />
                  ))}
                </Stack>
              </Box>
            ) : null}
            <TextField label="Headline" fullWidth value={fbLaunchForm.headline} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, headline: e.target.value }))} helperText="Selected headline — you can also edit manually" />
            <TextField label="Primary Text" fullWidth multiline minRows={3} value={fbLaunchForm.primary_text} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, primary_text: e.target.value }))} />
            <TextField label="Description" fullWidth value={fbLaunchForm.description} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, description: e.target.value }))} helperText="Short text shown below the headline (~30 chars)" />
            <Stack direction="row" spacing={2}>
              <TextField select label="CTA" sx={{ minWidth: 200 }} value={fbLaunchForm.cta_type} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, cta_type: e.target.value }))}>
                {['LEARN_MORE', 'SIGN_UP', 'SHOP_NOW', 'BOOK_TRAVEL', 'CONTACT_US', 'GET_OFFER', 'GET_QUOTE', 'SUBSCRIBE', 'CALL_NOW', 'APPLY_NOW', 'DOWNLOAD', 'ORDER_NOW'].map((cta) => (
                  <MenuItem key={cta} value={cta}>{cta.replace(/_/g, ' ')}</MenuItem>
                ))}
              </TextField>
              <TextField label="Destination URL" fullWidth value={fbLaunchForm.destination_url} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, destination_url: e.target.value }))} placeholder="https://..." helperText={fbLaunchForm.destination_url ? 'Auto-filled from client landing page' : 'No landing page found — enter manually (will be saved for future use)'} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Display Link" sx={{ minWidth: 250 }} value={fbLaunchForm.display_link} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, display_link: e.target.value }))} placeholder="hansons.com" helperText="Shown below the ad instead of the full URL" />
            </Stack>
            <TextField label="Facebook Page ID" fullWidth value={fbLaunchForm.page_id} onChange={(e) => setFbLaunchForm((prev) => ({ ...prev, page_id: e.target.value }))} helperText={fbLaunchForm.page_name ? `Auto-filled: ${fbLaunchForm.page_name}` : 'Leave blank to use Lead Gurus default page. New page IDs will be saved for future use.'} />

            {fbLaunchResult?.dry_run ? (
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Preview:</Typography>
                <Typography variant="body2"><strong>Ad Name:</strong> {fbLaunchResult.ad_name}</Typography>
                <Typography variant="body2"><strong>UTM Tags:</strong> {fbLaunchResult.url_tags}</Typography>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFbLaunchOpen(false); setFbLaunchResult(null); }}>Close</Button>
          <Button variant="outlined" disabled={fbLaunchLoading || fbLaunchResult?.success} onClick={async () => {
            setFbLaunchLoading(true);
            try {
              const res = await launchTaskToFacebook(fbLaunchTask.id, { ...fbLaunchForm, dry_run: true });
              setFbLaunchResult(res?.data);
            } catch (err) { setError(getApiErrorMessage(err, 'Preview failed')); }
            setFbLaunchLoading(false);
          }}>Preview</Button>
          <Button variant="contained" disabled={fbLaunchLoading || !fbLaunchForm.ad_account_id || !fbLaunchForm.ad_set_ids.length || !fbLaunchForm.headline || !fbLaunchForm.destination_url || fbLaunchResult?.success} onClick={async () => {
            setFbLaunchLoading(true);
            try {
              const res = await launchTaskToFacebook(fbLaunchTask.id, { ...fbLaunchForm, dry_run: false });
              setFbLaunchResult(res?.data);
              // Refresh task detail
              const updated = await fetchCreativeTask(fbLaunchTask.id);
              setDetailTask(updated.data);
            } catch (err) { setFbLaunchResult({ error: getApiErrorMessage(err, 'Facebook launch failed') }); }
            setFbLaunchLoading(false);
          }}>
            {fbLaunchLoading ? 'Launching...' : `Launch to ${fbLaunchForm.ad_set_ids.length || 0} Ad Set${fbLaunchForm.ad_set_ids.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== API INFO MODAL ========== */}
      <Dialog open={apiModalOpen} onClose={() => setApiModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>REST API - Designer Integration</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Alert severity="info">
              Use this API to programmatically fetch flight and task data. All endpoints require authentication via session cookie or API token.
            </Alert>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Base URL</Typography>
              <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                {window.location.origin}/api/creative-studio/designer-api/
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Endpoints</Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/designer-api/</Typography>
                  <Typography variant="caption" color="text.secondary">List all flights and tasks visible to the authenticated user. Staff sees all; designers see only assigned.</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/designer-api/?client_id=123</Typography>
                  <Typography variant="caption" color="text.secondary">Filter flights by client ID.</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/designer-api/?status=in_progress</Typography>
                  <Typography variant="caption" color="text.secondary">Filter by flight status: draft, in_progress, complete, cancelled.</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/designer-api/me/</Typography>
                  <Typography variant="caption" color="text.secondary">Get current user profile and designer info.</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/designer-api/flights/{'<id or code>'}/</Typography>
                  <Typography variant="caption" color="text.secondary">Get a single flight by numeric ID or flight code (e.g. FLT-0008), including all its tasks.</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/designer-api/tasks/{'<id or code>'}/</Typography>
                  <Typography variant="caption" color="text.secondary">Get a single task by numeric ID or task code (e.g. FLT-0008-T0042), including all details.</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>POST /api/creative-studio/designer-api/tasks/{'<id or code>'}/submit-link/</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Submit a Frame.io link to a task. Accepts numeric ID or task code. Body: {'{ "frame_io_url": "https://app.frame.io/..." }'}.
                    Auto-advances status: draft/assigned → in_progress, in_progress/needs_revision → submitted.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>GET /api/creative-studio/flights/{'<id>'}/export/</Typography>
                  <Typography variant="caption" color="text.secondary">Download all tasks in a flight as CSV.</Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Example Response</Typography>
              <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', overflow: 'auto', maxHeight: 300 }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify({
                  count: 1,
                  user: "designer@example.com",
                  flights: [{
                    id: 1,
                    flight_code: "FLT-0001",
                    name: "March Flight",
                    status: "in_progress",
                    client: "Example Co",
                    task_count: 5,
                    tasks: [{
                      id: 42,
                      task_code: "FLT-0001-T0042",
                      structured_name: "av=bmom&pp=slip&an=cur&hk=save30&at=vid&st=ugc&au=55+&cr=jake&v=1",
                      status: "assigned",
                      client: "Example Co",
                      vertical: "Bath",
                      audience: "55+",
                      avatar: "Busy Mom",
                      pain_point: "Slippery Tub",
                      angle: "Curiosity",
                      ad_type: "Video",
                      style: "UGC Ad",
                      hook_text: "What if your bathroom is the most dangerous room?",
                      script_text: "Full script text...",
                      designer: "Jake",
                      frame_io_url: null,
                      client_context: {
                        client_name: "Example Co",
                        website_url: "https://example.com",
                        logo_url: "https://dropbox.com/...",
                        notes: "Premium brand, focus on safety...",
                        ai_script_instructions: "Bath: Always mention lifetime warranty",
                        offers: [{ financing: "12 Months No/No", ad_offer: "50% Off Installation", vertical: "Bath" }],
                        client_asset_folder_url: "https://dropbox.com/...",
                        general_asset_folder_url: "https://dropbox.com/...",
                        recent_ad_examples_url: "https://dropbox.com/...",
                      },
                    }]
                  }]
                }, null, 2)}</pre>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Authentication</Typography>
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                Use an API token for programmatic access. Generate one below — it never expires and works with any HTTP client.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="contained"
                  disabled={apiTokenLoading}
                  onClick={async () => {
                    setApiTokenLoading(true);
                    try {
                      const res = await getDesignerAPIToken();
                      setApiToken(res.data);
                    } catch (_e) { /* silent */ }
                    setApiTokenLoading(false);
                  }}
                >
                  {apiTokenLoading ? 'Loading...' : apiToken ? 'Show Token' : 'Generate API Token'}
                </Button>
                {apiToken ? (
                  <Button
                    size="small"
                    color="warning"
                    disabled={apiTokenLoading}
                    onClick={async () => {
                      setApiTokenLoading(true);
                      try {
                        const res = await regenerateDesignerAPIToken();
                        setApiToken(res.data);
                      } catch (_e) { /* silent */ }
                      setApiTokenLoading(false);
                    }}
                  >
                    Regenerate
                  </Button>
                ) : null}
              </Stack>
              {apiToken ? (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Your API Token (keep this secret):</Typography>
                  <Box
                    sx={{ bgcolor: 'grey.900', color: 'success.light', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all', cursor: 'pointer', mt: 0.5 }}
                    onClick={() => { navigator.clipboard.writeText(apiToken.token); }}
                    title="Click to copy"
                  >
                    {apiToken.token}
                  </Box>
                  <Typography variant="caption" color="text.secondary">Click to copy</Typography>
                </Box>
              ) : null}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Quick Test (cURL)</Typography>
              <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', overflow: 'auto' }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{apiToken
                  ? `curl -H "Authorization: Token ${apiToken.token}" \\
  ${window.location.origin}/api/creative-studio/designer-api/`
                  : `curl -H "Authorization: Token YOUR_TOKEN" \\
  ${window.location.origin}/api/creative-studio/designer-api/`}</pre>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
